/**
 * contenu.ts : tout le texte du site, type, importe par les composants qui l'affichent.
 *
 * REGLE : chaque chaine de ce fichier est RECOPIEE mot pour mot depuis
 * `.documentation/specs/contenu-site.md`, en ouvrant le fichier. Aucun mot ne s'y ecrit, ne s'y
 * reformule et ne s'y raccourcit.
 *
 * ATTENTION : PLUS AUCUNE GARDE NE LE VERIFIE. La garde INV-TXT-001, qui comparait chaque chaine
 * de ce fichier a la spec et rougissait sur un mot d'ecart, a ete RETIREE du depot le 2026-08-11
 * sur decision de Wahid. La regle ci-dessus n'a pas change ; ce qui a change, c'est que plus rien
 * ne l'applique automatiquement. Elle se relit, et son controle final est AC-REC-001 a J-05.
 * Voir .documentation/dev/INVARIANTS.md section 3.0.
 *
 * ETAT AU JALON J-01 : ce fichier porte le texte des 23 sections de contenu-site.md et ses sept
 * libelles d'interface. AUCUNE section n'est RENDUE : le rendu est le perimetre de J-02. Ce fichier
 * est ce sur quoi les composants de sections s'appuieront.
 *
 * LE GRAS NE FIGURE PAS DANS LES CHAINES. Decision tracee dans
 * .tasks/01-socle-technique/progress.md, section Decisions et levees explicites, et dans la fiche
 * 01-02. Motif : une chaine portant `**un mot**` afficherait les asterisques en clair sur la page.
 * Le poids typographique (600) que contenu-site.md demande se porte dans la STRUCTURE de rendu.
 *
 * LA FORME RETENUE, pour que J-02 la trouve :
 *
 *   - Une chaine egale un FRAGMENT CONTIGU de la source : deux paragraphes separes par un titre
 *     dans la source ne se concatenent pas dans une meme chaine. Cette forme etait imposee par
 *     INV-TXT-001, retiree le 2026-08-11 ; elle est conservee parce qu'elle rend la relecture
 *     possible, chaque chaine se retrouvant telle quelle dans la spec.
 *   - Le decoupage se fait AU PARAGRAPHE. Un tableau de chaines, une chaine par paragraphe.
 *   - Un paragraphe qui commence par une amorce en gras se stocke en `{ intitule, texte }`. C'est
 *     la ou le gras de la source est porte par la structure : l'amorce se rend en poids 600, le
 *     texte en poids courant. Quand le gras est au milieu d'une phrase, le paragraphe reste une
 *     chaine unique et le poids se traite au rendu.
 *
 * CE QUI N'ENTRE PAS ICI, et ou cela vit :
 *
 *   - Les titres courts et les descriptions des douze placeholders, et le texte des deux schemas
 *     SVG : ils viennent de spec-design.md et NON de contenu-site.md. Jusqu'au 2026-08-11 ils ne
 *     pouvaient pas entrer ici, INV-TXT-001 comparant tout ce fichier a contenu-site.md. Cette
 *     garde ayant ete retiree, ils ENTRENT ici, sous la cle `visuels`, avec la mention de leur
 *     document source. Le sprint 02 les y ecrit.
 *   - Le texte des deux emails : pilotage-inscriptions.md. Il ne s'affiche pas sur le site.
 *   - Les notes de production de contenu-site.md (contrainte de mise en page, regle de publication,
 *     mise en page du mur des retours, reserve de publication). Elles s'adressent au developpeur,
 *     elles ne s'affichent pas.
 *
 * DEUX DECISIONS DE RECOPIE, prises en session autonome, reversibles, tracees dans la fiche 01-02 :
 *
 *   1. La section 20 est la seule des 23 a n'avoir aucun sous-titre `### Titre` dans la spec. Son
 *      `titre` reprend donc le nom que la spec donne a la section elle-meme, `FAQ`, sans un mot de
 *      plus. La section 23 n'a pas non plus de sous-titre `### Titre`, et un pied de page n'en
 *      demande pas : elle n'en porte aucun.
 *   2. La section 17 porte deux intitules de liste, `Ce qui est compris` et `Ce qui reste a votre
 *      charge`, ecrits dans la spec en titres de niveau 3. Ils sont recopies tels quels, parce que
 *      les deux listes ne se lisent pas sans eux. Aucun autre titre de niveau 3 de la spec n'est
 *      repris : les autres nomment une nature de bloc (Titre, Corps, Visuel), pas un texte lu.
 */

/** Un paragraphe qui s'ouvre sur une amorce mise en gras dans contenu-site.md. */
export type BlocIntitule = {
  /** L'amorce. Elle se rend en poids 600, jamais avec des asterisques. */
  readonly intitule: string;
  /**
   * La suite du paragraphe. Une chaine quand la spec n'y ecrit aucun gras, une SUITE DE FRAGMENTS
   * quand elle en ecrit un.
   *
   * ELLE ETAIT UNE CHAINE JUSQU'AU 2026-08-14, ET C'EST CE QUI A ROUVERT LA QUESTION 010 : dix blocs
   * de cette forme portent du gras au milieu de leur phrase dans contenu-site.md, et une chaine ne
   * peut pas en garder la position. La conversion mecanique du 2026-08-13 n'avait pu traiter que les
   * champs deja stockes en paragraphes ; ces dix blocs lui etaient hors d'atteinte, et douze gras y
   * restaient perdus. Les trois derniers etaient dans `LeFormateur.activites`, pour la meme raison.
   */
  readonly texte: Paragraphe;
};

/**
 * UN MORCEAU DE PARAGRAPHE, marque ou non. C'est ce qui porte le gras ECRIT AU MILIEU D'UNE PHRASE
 * par contenu-site.md, et c'est la fermeture de la question 010, ouverte le 2026-08-11.
 *
 * POURQUOI CETTE FORME ET PAS UNE CHAINE A ASTERISQUES : une chaine portant `**un mot**`
 * afficherait les asterisques en clair sur la page, et une chaine accompagnee d'une liste de mots a
 * mettre en gras serait fausse des qu'un mot figure deux fois dans le paragraphe. Ici la POSITION
 * est portee par la structure.
 *
 * CE QUI RESTE VERIFIABLE, et c'est le point : la concatenation des fragments rend la phrase de la
 * spec, caractere pour caractere. La recopie se controle donc mecaniquement, ce qu'une liste de mots
 * ne permettait pas.
 *
 * LES 50 PARAGRAPHES CONCERNES ONT ETE CONVERTIS MECANIQUEMENT depuis contenu-site.md le
 * 2026-08-13, pas a la main, PUIS DIX BLOCS DE PLUS LE 2026-08-14, par le meme procede. Les dix
 * derniers sont ceux dont le gras OUVRE le bloc : leur amorce se garde en `intitule`, et leur suite
 * passe ici.
 *
 * QUEL GRAS SE REND, ET LEQUEL NE SE REND PAS. La regle est dans spec-design.md v4, section « La
 * mise en valeur des mots-cles » : une amorce se rend en gras quelle que soit sa longueur, un gras
 * ecrit AU MILIEU d'une phrase se rend en gras jusqu'a TROIS MOTS, et au-dela il se rend en poids
 * courant. Un gras non rendu garde quand meme son fragment a lui : sa POSITION reste ecrite, seule
 * sa marque tombe. C'est ce qui permet de revenir sur la regle sans refaire la recopie.
 */
export type Fragment = string | { readonly gras: string };

/** Un paragraphe : une chaine simple, ou une suite de fragments quand il porte du gras interne. */
export type Paragraphe = string | readonly Fragment[];

/** Une citation de participant, avec son auteur. */
export type Citation = {
  readonly texte: string;
  readonly auteur: string;
};

/** Un lien : son libelle vient de la spec, sa cible est une valeur technique. */
export type Lien = {
  readonly libelle: string;
  readonly href: string;
};

/** Un champ du formulaire de la section 19. */
export type ChampFormulaire = {
  readonly libelle: string;
  readonly type: string;
  readonly obligatoire: boolean;
};

/** Un groupe de cases a cocher du champ des disponibilites. */
export type GroupeDeCases = {
  readonly intitule: string;
  readonly options: readonly string[];
};

/** 1. Haut de page. */
export type HautDePage = {
  readonly titre: string;
  readonly sousTitre: Paragraphe;
  readonly bouton: string;
  readonly sousLeBouton: string;
  readonly visuels: readonly string[];
};

/** 2. Qu'est-ce que le Harness ? */
export type QuEstCeQueLeHarness = {
  readonly titre: string;
  readonly corps: readonly Paragraphe[];
};

/** 3. Bandeau des sept valeurs. */
export type BandeauDesValeurs = {
  readonly titre: string;
  readonly items: readonly BlocIntitule[];
};

/** 4. Preuve sociale. */
/** 5. Le formateur. */
export type LeFormateur = {
  readonly titre: string;
  readonly corpsAvantLaListe: readonly Paragraphe[];
  /**
   * Les trois activites. Chacune S'OUVRE SUR UNE AMORCE EN GRAS dans contenu-site.md, et la
   * deuxieme est en gras d'un bout a l'autre : elles se stockent donc en fragments, et non en
   * chaines plates comme jusqu'au 2026-08-14.
   */
  readonly activites: readonly Paragraphe[];
  readonly corpsApresLaListe: readonly Paragraphe[];
  readonly lien: Lien;
  readonly visuels: readonly string[];
};

/** 6, 7, 10, 12, 15, 18 : une section faite d'un titre et de paragraphes. */
export type SectionDeParagraphes = {
  readonly titre: string;
  readonly corps: readonly Paragraphe[];
  readonly visuels: readonly string[];
};

/** 8. Le Harness. */
export type LeHarness = {
  readonly titre: string;
  readonly corps: readonly Paragraphe[];
  readonly lesTroisPieces: readonly BlocIntitule[];
  readonly conclusion: readonly Paragraphe[];
  readonly visuels: readonly string[];
};

/** 9. La boucle de retroaction. */
export type LaBoucleDeRetroaction = {
  readonly titre: string;
  readonly corps: readonly Paragraphe[];
  readonly lesQuatreTemps: readonly BlocIntitule[];
  readonly conclusion: readonly Paragraphe[];
  readonly visuels: readonly string[];
};

/** 11. Les techniques. */
export type LesTechniques = {
  readonly titre: string;
  readonly introduction: Paragraphe;
  readonly lesHuitGestes: readonly BlocIntitule[];
  readonly conclusion: readonly Paragraphe[];
  readonly visuels: readonly string[];
};

/** 13. L'autonomie. */
export type LAutonomie = {
  readonly titre: string;
  readonly corps: readonly Paragraphe[];
  readonly ceQueVousGardez: BlocIntitule;
  readonly visuels: readonly string[];
};

/** 14. Le deroule de la journee. */
export type LeDerouleDeLaJournee = {
  readonly titre: string;
  readonly introduction: readonly Paragraphe[];
  readonly lesTroisTemps: readonly BlocIntitule[];
  readonly bandeauDeReperes: readonly string[];
  readonly visuels: readonly string[];
};

/** 16. Ce qu'il faut preparer. */
export type CeQuIlFautPreparer = {
  readonly titre: string;
  readonly premierPoint: BlocIntitule;
  readonly verification: string;
  readonly secondPoint: BlocIntitule;
  readonly encadre: BlocIntitule;
  readonly lien: Lien;
};

/** 17. Le prix. */
export type LePrix = {
  readonly titre: string;
  readonly intituleCompris: string;
  readonly compris: readonly string[];
  readonly intituleACharge: string;
  readonly aVotreCharge: readonly string[];
  readonly sousLeBloc: string;
  readonly mentionPaiement: Paragraphe;
  readonly lien: Lien;
};

/** 19. Le formulaire. */
/**
 * 17. Le formulaire.
 *
 * ⚠️ ELLE N'A PLUS DE CHAMPS DEPUIS LE 2026-08-15, TACHE 07-07, ET C'EST UNE DECISION DE WAHID :
 * « les six champs quittent la page d'accueil, son bouton ouvre le parcours ». Le motif qu'il a
 * donne : un seul chemin d'inscription, donc un seul endroit ou un defaut peut se cacher.
 *
 * CE QUE CE TYPE PORTAIT ENCORE LA VEILLE : `champs`, `disponibilites`, `consentement`,
 * `messageApresEnvoi` et `messageEchecTechnique`. `contenu-site.md` v5 a supprime leur texte le meme
 * jour, et les six libelles vivent desormais sous `inscription.etapes.vosCoordonnees`, mot pour mot.
 *
 * IL GARDE SON TITRE, SON SOUS-TITRE ET SON BOUTON, et le bouton mene a `/inscription` :
 * `AC-INS-008` compte zero champ de saisie dans cette section et verifie la cible du bouton.
 */
export type LeFormulaire = {
  readonly titre: string;
  readonly sousTitre: Paragraphe;
  readonly bouton: string;
};

/**
 * 20. FAQ.
 *
 * PAS DE CHAMP `titre`, ET C'EST VOULU : contenu-site.md section 20 n'ouvre aucun bloc « Titre »,
 * comme la section 23. Le detail et la question ouverte sont en tete de components/sections/Faq.tsx.
 */
export type Faq = {
  readonly questions: readonly {
    readonly question: string;
    readonly reponse: Paragraphe;
  }[];
};

/** 21. Le mur des retours. */
export type LesTemoignages = {
  readonly titre: string;
  readonly citations: readonly Citation[];
};

/** 22. Dernier appel. */
export type DernierAppel = {
  readonly titre: string;
  readonly corps: Paragraphe;
  readonly bouton: string;
  readonly sousLeBouton: string;
};

/** 23. Pied de page. */
export type PiedDePage = {
  readonly ligne: string;
  readonly contact: {
    readonly intitule: string;
    readonly email: string;
    readonly telephone: string;
  };
  readonly linkedin: Lien;
  /**
   * Les deux pages annexes que contenu-site.md section 23 nomme.
   *
   * LES LIBELLES VIENNENT DE contenu-site.md, LES CHEMINS DE spec-technique.md : son arborescence
   * ecrit `app/mentions-legales/page.tsx` et `app/confidentialite/page.tsx`, ce qui donne ces deux
   * routes dans le routeur de Next. Aucun des deux chemins n'est invente ici.
   *
   * LES DEUX PAGES REPONDENT EN 404 JUSQU'A J-04, qui les ecrit. Le lien reste : AC-CNT-022 exige
   * que le pied de page porte les liens de la spec, et AC-REC-006, qui interdit un lien mort, se
   * joue a J-05. C'est declare non fait dans .tasks/02-la-page/progress.md.
   */
  readonly pagesAnnexes: {
    readonly intitule: string;
    readonly mentionsLegales: Lien;
    readonly confidentialite: Lien;
  };
};

/** Les 23 sections de la page, dans l'ordre de contenu-site.md. */
export type Sections = {
  readonly hautDePage: HautDePage;
  readonly quEstCeQueLeHarness: QuEstCeQueLeHarness;
  readonly bandeauDesValeurs: BandeauDesValeurs;
  readonly leFormateur: LeFormateur;
  readonly leChangementDeMindset: SectionDeParagraphes;
  readonly comprendreLaMachine: SectionDeParagraphes;
  readonly leHarness: LeHarness;
  readonly laBoucleDeRetroaction: LaBoucleDeRetroaction;
  readonly laMethodologie: SectionDeParagraphes;
  readonly lesTechniques: LesTechniques;
  readonly lAutonomie: LAutonomie;
  readonly leDerouleDeLaJournee: LeDerouleDeLaJournee;
  readonly pourQui: SectionDeParagraphes;
  readonly ceQuIlFautPreparer: CeQuIlFautPreparer;
  readonly lePrix: LePrix;
  readonly lesDates: SectionDeParagraphes;
  readonly leFormulaire: LeFormulaire;
  readonly faq: Faq;
  readonly lesTemoignages: LesTemoignages;
  readonly dernierAppel: DernierAppel;
  readonly piedDePage: PiedDePage;
};

/**
 * Les libelles qui vivent hors des sections. Source : contenu-site.md, Les libelles d'interface.
 *
 * ILS SONT HUIT DEPUIS LE 2026-08-16, ET AC-SIT-010 COMPTE HUIT. Ce critere, livre a J-01, asserte
 * que `contenu.interface` porte exactement les cles du tableau de la spec, dans son ordre. Toute
 * chaine qui ne vient pas de cette section de contenu-site.md le ferait rougir.
 *
 * LA HUITIEME EST LE LIBELLE DU LIEN D'EVITEMENT, ET IL A VECU SOUS UNE CLE A PART JUSQU'ICI.
 * `spec-technique.md` § L'accessibilite exige le lien depuis le 2026-08-11, et contenu-site.md n'en
 * nommait pas le libelle : il affichait donc la mention `[a completer : ...]`, interim de Q-007,
 * range sous `accessibilite` pour que le compte de sept reste juste. Wahid a donne le libelle mot
 * pour mot le 2026-08-16, contenu-site.md v9 l'ecrit, et le motif du rangement a part n'existe plus.
 * La cle `accessibilite` est supprimee avec lui : elle ne portait que cette chaine.
 */
export type LibellesInterface = {
  /** Titre de l'onglet du navigateur. */
  readonly titreOnglet: string;
  /** Description pour les moteurs et le partage, page d'accueil. */
  readonly description: string;
  /**
   * Description de la page d'inscription, pour les moteurs.
   *
   * ECRITE DANS `contenu-site.md` DEPUIS LA V10, du 2026-08-17 : Wahid a delegue au dev les
   * descriptions des trois pages en repondant a la question 024. Jusque-la, `/inscription` servait
   * son propre sous-titre, ecrit pour etre lu SUR la page. `AC-ANX-016` exige des descriptions
   * distinctes, `AC-CNT-028` exige qu'elles soient celles du document.
   */
  readonly descriptionInscription: string;
  /** Description de la page de contact, pour les moteurs. Meme origine que la precedente. */
  readonly descriptionContact: string;
  /**
   * Description de la page des mentions legales, pour les moteurs.
   *
   * ECRITE EN MEME TEMPS QUE LA PAGE, `contenu-site.md` v11 du 2026-08-17. `AC-ANX-016` exige un
   * titre et une description propres a CHAQUE page servie, et les pages servies passent de trois a
   * cinq le jour ou les deux pages annexes existent.
   */
  readonly descriptionMentionsLegales: string;
  /** Description de la page de confidentialite, pour les moteurs. Meme origine. */
  readonly descriptionConfidentialite: string;
  /** Bouton en cours d'envoi. */
  readonly boutonEnvoiEnCours: string;
  /** Champ obligatoire vide. */
  readonly champObligatoireVide: string;
  /** Email mal forme. */
  readonly emailMalForme: string;
  /** Consentement decoche. */
  readonly consentementDecoche: string;
  /** Lien de retour depuis les pages annexes. */
  readonly lienRetour: string;
  /**
   * Lien d'evitement, le premier element atteint par la touche Tabulation.
   *
   * `spec-technique.md` § L'accessibilite exige « un lien d'evitement mene directement au contenu ».
   * Son libelle est ecrit dans contenu-site.md § Les libelles d'interface depuis la v9, du
   * 2026-08-16 : Wahid l'a donne mot pour mot en repondant a la question Q-007. La fiche 02-02
   * annoncait deja cette cle, `interface.lienEvitement`, et elle avait ete rangee ailleurs le temps
   * que le libelle arrive.
   */
  readonly lienEvitement: string;
};

/**
 * Un emplacement d'illustration, PH-01 a PH-12.
 *
 * DEUX DOCUMENTS SOURCES, et c'est voulu : `titre`, `rapport` et `alt` viennent de
 * spec-design.md section « Les placeholders » ; `description` vient du bloc « Visuel » de la
 * section correspondante de contenu-site.md.
 *
 * POURQUOI DEUX TEXTES : spec-design.md designe deux chaines differentes comme « la description ».
 * Son paragraphe d'ouverture nomme « la description ecrite dans contenu-site.md », qui sont des
 * phrases courtes ; le tableau du meme paragraphe porte une colonne « Description a afficher »
 * avec des textes de trois a quatre lignes. La regle de rendu du meme paragraphe tranche dans un
 * sens : la description se rend « sur deux lignes maximum », ce que les textes du tableau ne
 * tiennent pas. Ecart trace en Q-008 dans .documentation/REMONTEES.md, avec son interim :
 * `description` s'affiche, `alt` sert d'alternative textuelle, ce que spec-design.md prevoit deja
 * (« la description sert d'alternative textuelle des que l'image reelle arrive »). Les echanger
 * est une ligne.
 */
export type Emplacement = {
  /** Rapport de forme, colonne « Rapport » du tableau de spec-design.md. */
  readonly rapport: string;
  /** Titre court affiche au centre de l'emplacement, colonne « Titre court ». */
  readonly titre: string;
  /** Phrase courte affichee sous le titre. Source : bloc « Visuel » de contenu-site.md. */
  readonly description: string;
  /** Alternative textuelle, colonne « Description a afficher » de spec-design.md. */
  readonly alt: string;
};

/** Un bloc ou une etape d'un schema : l'intitule en gras dans la spec, puis son detail. */
export type EntreeDeSchema = {
  /** L'intitule. Il se rend en poids 600, jamais avec des asterisques. */
  readonly intitule: string;
  /** Ce que la spec ecrit apres le point median. */
  readonly detail: string;
};

/** SVG-01, spec-design.md section « SVG-01, les trois pieces du Harness ». */
export type SchemaHarness = {
  /** Le titre accessible du schema. Source : contenu-site.md section 8, bloc Visuels. */
  readonly titreAccessible: string;
  readonly blocs: readonly EntreeDeSchema[];
  /** Le mot porte par le quatrieme element, celui qui rassemble les trois blocs. */
  readonly rassemblement: string;
  /** La mention placee sous les trois blocs. */
  readonly mention: string;
};

/** SVG-02, spec-design.md section « SVG-02, la boucle en quatre temps ». */
export type SchemaBoucle = {
  /** Le titre accessible du schema. Source : contenu-site.md section 9, bloc Visuels. */
  readonly titreAccessible: string;
  readonly etapes: readonly EntreeDeSchema[];
  /** La mention portee par la fleche qui ferme la boucle, de l'opposition vers les objectifs. */
  readonly mention: string;
};

/** Le texte des douze emplacements d'illustration et des deux schemas. */
export type Visuels = {
  readonly placeholders: {
    readonly PH01: Emplacement;
    readonly PH03: Emplacement;
    readonly PH04: Emplacement;
    readonly PH05: Emplacement;
    readonly PH06: Emplacement;
    readonly PH07: Emplacement;
    readonly PH08: Emplacement;
    readonly PH09: Emplacement;
    readonly PH11: Emplacement;
    readonly PH12: Emplacement;
  };
  readonly schemas: {
    readonly harness: SchemaHarness;
    readonly boucle: SchemaBoucle;
  };
};

/**
 * UNE CHAINE ECRITE PAR LE DEV, ET NON RECOPIEE D'UNE SPEC.
 *
 * C'est le seul type de ce fichier dont les valeurs ne viennent d'aucun document. Chaque entree
 * porte la section qu'elle sert et l'origine de sa tournure, pour que la revue de Wahid se fasse
 * en lisant ce bloc de haut en bas, sans rouvrir un autre fichier.
 */
export type ChaineDeDev = {
  /** Ce qui s'affiche. */
  readonly texte: string;
  /** La section servie, par son numero dans contenu-site.md, ou le role dans l'interface. */
  readonly sert: string;
  /** D'ou vient la tournure : un titre de section de la spec, ou une tournure du dev. */
  readonly origine: string;
};

/**
 * LES 29 CHAINES ECRITES PAR LE DEV. C'est la SEULE derogation a INVARIANTS.md section 1, la regle
 * qui interdit d'afficher un mot absent de contenu-site.md.
 *
 * D'OU ELLE VIENT : Wahid l'a accordee le 2026-08-12, apres avoir ete averti que la barre de
 * navigation et les sur-titres demandaient 29 mots qui n'existent dans aucune spec. Sa reponse :
 * « Improvise pour les 2 points de contenu, tu connais le projet et son objectif. Je suis presse
 * d'en finir, je ferai une grande revision une fois que tout est implemente. » La decision est
 * tracee dans .documentation/REMONTEES.md, questions closes, entree Q-015 et Q-016.
 *
 * TROIS REGLES L'ENCADRENT, ET AC-DSN-023 LES VERIFIE :
 *
 *   1. Les 29 chaines sont ICI et nulle part ailleurs. Aucune n'est ecrite dans un composant, et
 *      aucune n'est melangee aux chaines venues de contenu-site.md.
 *   2. Chacune porte la section qu'elle sert et l'origine de sa tournure.
 *   3. Un sur-titre reprend le vocabulaire de sa section. IL NE PRONONCE NI PROMESSE, NI CHIFFRE,
 *      NI DATE, NI BENEFICE que contenu-site.md n'ecrit pas. Le dev choisit une tournure, il
 *      n'ajoute pas d'argument commercial.
 *
 * LA SOURCE LA PLUS CONSERVATRICE A ETE PRISE A CHAQUE FOIS QU'ELLE EXISTAIT : dix-sept des
 * vingt-trois sur-titres sont le NOM QUE contenu-site.md DONNE A SA PROPRE SECTION, dans ses
 * titres `## N. ...`. Q-014 a etabli que ces noms sont de la numerotation et non du contenu
 * affichable : les employer en sur-titre est un choix du dev, et c'est pourquoi ils sont ici.
 * Les six autres sont marques `tournure du dev` : ce sont ceux dont le nom de section decrit
 * un role de gabarit (« Haut de page », « Preuve sociale », « Le formulaire », « Dernier appel »,
 * « Pied de page ») ou un mot que la spec n'ecrit qu'en sigle (« FAQ »).
 *
 * CE QUE CETTE DEROGATION NE COUVRE PAS : les 16 mentions `[a completer : ...]` de la spec. Elles
 * restent visibles et ne se comblent pas.
 */
export type RedactionDev = {
  /** Les huit libelles de la barre de navigation et de la barre d'action collante. */
  readonly navigation: {
    readonly nomProduit: ChaineDeDev;
    readonly ancres: readonly (ChaineDeDev & { readonly cible: string })[];
    /**
     * L'ENTREE DE CONTACT DE LA BARRE HAUTE, AJOUTEE LE 2026-08-16. Decision de Wahid sur la
     * question Q-018 : le contact entre dans la barre de navigation, qui passe de six intitules
     * visibles a sept.
     *
     * ELLE N'EST PAS DANS `ancres`, ET CE N'EST PAS UN RANGEMENT DE CONFORT : `AC-DSN-023` exige
     * que les quatre ancres visent une SECTION de la page, donc une cible commencant par `#`. Celle
     * du contact vise une autre page, `/contact`. La mettre dans le tableau aurait fait rougir ce
     * controle, ou l'aurait force a admettre une exception qui aurait laisse passer une ancre
     * cassee.
     *
     * LE PIED DE PAGE GARDE LE SIEN. Wahid a demande une entree PLUS VISIBLE, pas un deplacement, et
     * le pas `RP-03-1` du parcours `PU-03` atteint la page de contact depuis le pied de page.
     */
    readonly contact: ChaineDeDev & { readonly cible: string };
    readonly prixBarreMobile: ChaineDeDev;
    /**
     * Ce qui s'affiche a droite de la barre haute SUR TELEPHONE, la ou les quatre ancres et le
     * bouton disparaissent. Ajoute le 2026-08-14.
     *
     * LE TEXTE EST DE WAHID, PAS DU DEV : il l'a ecrit mot pour mot dans sa demande, « vue mobile :
     * ajouter Formation Harness IA a droite ». Il vit ici parce que c'est l'endroit unique ou toutes
     * les chaines qui ne viennent pas de contenu-site.md se relisent, et non parce que le dev l'a
     * redigee.
     */
    readonly mentionMobile: ChaineDeDev;
  };
  /** Les 23 sur-titres, dans l'ordre des sections de contenu-site.md. */
  /**
   * LE TEXTE QUI S'AFFICHE A L'INTERIEUR DES HUIT ECRANS DESSINES.
   *
   * DEUXIEME DEROGATION A INVARIANTS.md section 1, deleguee par Wahid le 2026-08-13 : « mets-y du
   * texte, qu'elles semblent veridiques ». La premiere reponse, le matin meme, etait le texte
   * ABSTRAIT, des barres au lieu de mots ; une revue de direction artistique l'a jugee insuffisante,
   * « huit fois le meme squelette vide ».
   *
   * QUATRE REGLES LA BORNENT, ecrites dans REMONTEES.md section 10 : ces chaines vivent ICI et nulle
   * part ailleurs, chacune porte l'illustration qu'elle sert, aucune ne prononce de promesse, de
   * chiffre ou de date que contenu-site.md n'ecrit pas, et AC-ILL-001 exige que tout texte rendu par
   * une illustration vienne de cette cle.
   *
   * CE QU'ELLES NOMMENT : ce qu'un ecran de travail affiche. Des noms de fichiers, des intitules de
   * colonne, des libelles d'interface. Le dev meuble un ecran, il n'ajoute pas d'argument commercial.
   */
  /**
   * Les deux libelles des fleches du carrousel de temoignages.
   *
   * Ils ne s'affichent pas a l'ecran : ce sont les noms accessibles des deux liens, lus par un
   * lecteur d'ecran. Sans eux, deux liens de la page n'auraient aucun nom, ce qui est un manquement
   * d'accessibilite. Ils suivent la delegation des libelles de navigation du 2026-08-12.
   */
  /**
   * Le titre de la table des matieres. Il ne vient d'aucune spec : c'est le dev qui l'ecrit, sous la
   * meme cle que les libelles de navigation, delegues par Wahid le 2026-08-12. Deux mots, repris du
   * vocabulaire du document, sans promesse ni argument.
   */
  readonly sommaire: {
    readonly titre: string;
  };

  readonly carrousel: {
    readonly precedent: string;
    readonly suivant: string;
    readonly vue: string;
  };

  readonly illustrations: {
    readonly [identifiant: string]: {
      readonly sert: string;
      readonly mots: readonly string[];
    };
  };
  readonly surtitres: {
    readonly hautDePage: ChaineDeDev;
    readonly quEstCeQueLeHarness: ChaineDeDev;
    readonly bandeauDesValeurs: ChaineDeDev;
    readonly leFormateur: ChaineDeDev;
    readonly leChangementDeMindset: ChaineDeDev;
    readonly comprendreLaMachine: ChaineDeDev;
    readonly leHarness: ChaineDeDev;
    readonly laBoucleDeRetroaction: ChaineDeDev;
    readonly laMethodologie: ChaineDeDev;
    readonly lesTechniques: ChaineDeDev;
    readonly lAutonomie: ChaineDeDev;
    readonly leDerouleDeLaJournee: ChaineDeDev;
    readonly pourQui: ChaineDeDev;
    readonly ceQuIlFautPreparer: ChaineDeDev;
    readonly lePrix: ChaineDeDev;
    readonly lesDates: ChaineDeDev;
    readonly leFormulaire: ChaineDeDev;
    readonly faq: ChaineDeDev;
    readonly lesTemoignages: ChaineDeDev;
    readonly dernierAppel: ChaineDeDev;
    readonly piedDePage: ChaineDeDev;
  };
  /**
   * LA TROISIEME DELEGATION DE REDACTION, accordee par Wahid le 2026-08-14.
   *
   * Question posee : « quand une session sera proposee a la reservation, la page devra afficher des
   * mots qui n'existent dans aucune spec ». Reponse : « tu delegues, comme pour les mentions
   * legales ». Les quatre chaines ci-dessous sont donc ecrites par le dev, et elles se revisent avec
   * les 28 autres a la grande relecture de contenu.
   *
   * ELLES NE S'AFFICHENT QUE S'IL EXISTE AU MOINS UNE SESSION OUVERTE. Tant qu'il n'y en a aucune, la
   * page publique est exactement celle d'avant : aucun de ces mots n'est servi.
   */
  readonly session: {
    readonly intitule: ChaineDeDev;
    readonly aide: ChaineDeDev;
    readonly sansPreference: ChaineDeDev;
    /** Le gabarit d'une ligne de session. `{jour}`, `{lieu}`, `{debut}` et `{fin}` y sont remplaces. */
    readonly gabarit: ChaineDeDev;
    /**
     * Le seul fragment du parcours d'inscription qui ne vienne pas de `contenu-site.md`.
     *
     * IL EST ICI ET NON SOUS `inscription` PARCE QUE C'EST LA REGLE : ce qui n'est ecrit dans aucun
     * document opposable vit sous `redactionDev`, avec la section qu'il sert et l'origine de sa
     * tournure, et Wahid le relit avec les autres. `AC-DSN-023` compte les chaines de cette cle.
     */
    readonly gabaritHoraire: ChaineDeDev;
  };
};

/**
 * Les deux emails automatiques, recopies de `pilotage-inscriptions.md` § Les emails, points 1 et 2.
 *
 * POURQUOI ILS NE SONT PAS SOUS `redactionDev` : ils ne sont pas ecrits par le dev. Leur texte est
 * dans une spec, et il se recopie comme celui de la page. `redactionDev` ne porte que les 28 chaines
 * que Wahid a deleguees le 2026-08-12, et `AC-DSN-023` en compte exactement 28.
 *
 * LES ACCOLADES SONT DANS LA SPEC, pas inventees ici : elle ecrit « Bonjour {Prenom} » et
 * « Nouvelle demande de place · {Prenom et nom} · {N} au total ».
 */
export type Emails = {
  /**
   * L'email 1, REECRIT LE 2026-08-15 PAR LA TACHE 07-07, sur `pilotage-inscriptions.md` v4 § 1.
   *
   * CE QUI A CHANGE, ET POURQUOI LE DOCUMENT LE DIT : depuis le parcours en cinq etapes, la personne
   * a deja choisi sa session quand cet email lui arrive. Le texte de la v3 lui promettait un email a
   * venir qui n'a plus lieu d'etre.
   *
   * ⚠️ SON CORPS EST REECRIT LE 2026-08-16 PAR LA TACHE 08-05, sur `pilotage-inscriptions.md` v7
   * § 1 : les deux lignes qui portaient les coordonnees bancaires et le montant sont remplacees par
   * l'annonce de l'appel de Wahid.
   *
   * LE CORPS PORTE UN PARAGRAPHE QUI VARIE, ET IL N'EN EXISTE QUE DEUX VERSIONS. Le document les
   * nomme A et B : A quand une session a ete retenue, B quand aucune ne l'a ete.
   */
  readonly confirmation: {
    readonly objet: string;
    /** Le corps, `{prenom}` et `{paragraphe}` a remplacer. Les deux mentions y sont recopiees. */
    readonly corps: string;
    /** Version A, quand une session a ete choisie. `{date}` et `{ville}` s'y remplacent. */
    readonly paragrapheAvecSession: string;
    /**
     * Version B, quand aucune session n'a ete choisie, EN DEUX MORCEAUX.
     *
     * ⚠️ LE DECOUPAGE EST L'INTERIM DE `Q-017`, ET IL N'AJOUTE AUCUN MOT. Le document ecrit la
     * version B en une phrase qui suppose des disponibilites cochees : « vous avez indique etre
     * disponible : {Dispo quand}, {Dispo jour} ». Le champ est FACULTATIF, et rien n'est ecrit pour
     * qui ne coche rien. `disponibilites` n'est donc pose que s'il y a quelque chose a y mettre, et
     * `suite` part seule sinon. Retirer des mots de la spec n'en invente aucun.
     */
    readonly paragrapheSansSession: {
      readonly disponibilites: string;
      readonly suite: string;
    };
  };
  /**
   * L'email 2, REECRIT LE MEME JOUR, sur `pilotage-inscriptions.md` v4 § 2.
   *
   * SON CORPS N'EST PAS UNE CITATION, ET LE DOCUMENT NE LUI EN DONNE PAS : il le DECRIT, « le
   * contenu reprend les colonnes du participant et de l'inscription, la session choisie ou la
   * mention qu'aucune ne l'a ete, la remarque si elle est saisie, et {N} ». Les intitules ci-dessous
   * sont donc des etiquettes de donnee, ecrites par le dev sur la delegation du 2026-08-15, et elles
   * entrent dans la relecture de Wahid avec le reste.
   */
  readonly notification: {
    readonly objet: string;
    /** Les intitules des lignes de donnees, dans l'ordre d'affichage. */
    readonly champs: {
      readonly prenomNom: string;
      readonly email: string;
      readonly telephone: string;
      readonly ville: string;
      readonly session: string;
      readonly disponibilites: string;
      readonly remarque: string;
      readonly consentement: string;
      readonly source: string;
    };
    /** Ce qui s'affiche a la ligne « Session » quand la personne n'en a retenu aucune. */
    readonly sessionAucune: string;
    readonly pied: string;
  };
  /**
   * Les quatre emails des gestes de pilotage, recopies de `pilotage-inscriptions.md` v5.
   *
   * ⚠️ LEURS ACCOLADES SONT CELLES DU DOCUMENT, `{Prénom}` AVEC SON ACCENT ET SA MAJUSCULE, et non
   * `{prenom}` comme les deux emails du dessus. Ce n'est pas une inconstance : `AC-ADM-044` compare
   * la chaîne envoyée au document, et il fait la comparaison en remplacant les MEMES marqueurs des
   * deux côtés. Un marqueur renomme ici obligerait le controle a tenir une table de correspondance,
   * et c'est cette table qui se tromperait le jour ou la spec change un mot.
   */
  readonly pilotage: {
    /** Le geste « accepter » une inscription. `pilotage-inscriptions.md` v5 § Acceptation. */
    readonly acceptation: ModeleDEmail;
    /** Le geste « refuser » une inscription. § Refus, apres confirmation. */
    readonly refus: ModeleDEmail;
    /** Le geste « annuler » une session. § Annulation de session, apres confirmation. */
    readonly annulation: ModeleDEmail;
    /**
     * Le geste « confirmer » une session. § 3. La session se confirme.
     *
     * ⚠️ IL PORTE DEUX VERSIONS DEPUIS LE 2026-08-16, tache 08-04, et c'est le seul des six a en
     * porter. Motif ecrit dans `pilotage-inscriptions.md` v6 § 3 : depuis que la confirmation d'une
     * session arrive APRES les paiements, la version unique demandait a une personne qui avait deja
     * vire de virer pour reserver la place qu'elle tenait deja. La version A va aux inscriptions
     * portant une date de virement, la version B aux autres, et `AC-PAY-025` compte les deux envois
     * separement.
     */
    readonly sessionConfirmee: ModeleDEmailADeuxVersions;
    /**
     * L'email 4, « Votre place est réservée ». § 4. Le virement est reçu.
     *
     * IL PART DU GESTE « NOTER LE VIREMENT », ET DE LUI SEUL. Son corps est écrit en v6, le
     * 2026-08-15 : la v5 n'en donnait que l'objet et une phrase disant ce qu'il porte.
     */
    readonly placeReservee: ModeleDEmailAuSolde;
    /**
     * L'email du VIREMENT SUIVANT, ne le 2026-08-19. § Virement suivant, automatique.
     *
     * IL PART A CHAQUE VIREMENT QUI N'EST PAS LE PREMIER de son inscription, et l'email 4 ne part
     * plus dans ce cas.
     */
    readonly virementSuivant: ModeleDEmailAuSolde;
    /**
     * L'email d'annulation faute de place. § Annulation faute de place, après confirmation.
     *
     * IL PART DE LA CONFIRMATION DE L'ÉVICTION, un par personne présentée. Il n'existait dans
     * aucune version précédente du document.
     */
    readonly annulationFauteDePlace: ModeleDEmail;
  };
  /**
   * LES DEUX ALERTES VERS L'ADRESSE D'ADMINISTRATION, nees le 2026-08-16 avec la tache 08-04.
   *
   * ELLES NE VONT JAMAIS A UN PARTICIPANT, et c'est ce qui les distingue des six modeles du dessus :
   * `pilotage-inscriptions.md` v6 § Les deux alertes ecrit « Les deux vont a l'adresse
   * d'administration, jamais a un participant ». Leur destinataire vit dans `EMAIL_NOTIFICATION`,
   * comme celui de la notification de demande, et il ne s'ecrit dans aucun fichier du depot.
   *
   * LEURS MARQUEURS NE SONT PAS CEUX DES SIX AUTRES : elles ne portent aucun `{Prénom}`, personne
   * n'etant salue, et elles portent des NOMBRES, `{payes}`, `{places}`, `{minimum}` et `{jours}`.
   * C'est pourquoi `lib/courrier.ts` les envoie par une fonction a part plutot que par
   * `envoieUnEmailDePilotage`, dont les quatre marqueurs se prennent sur une personne.
   */
  readonly alertes: {
    /** § Le minimum est atteint. Il part du geste « noter le virement », une fois par session. */
    readonly minimumAtteint: ModeleDEmail;
    /** § Le préavis. Il part de la tache planifiee, le nombre de jours d'avance avant la date. */
    readonly preavis: ModeleDEmail;
    /**
     * § Une annulation par l'inscrit, nee le 2026-08-20. Elle part de la page d'annulation.
     *
     * ⚠️ ELLE NE CONFIRME RIEN ET N'ANNULE RIEN, comme les deux autres : l'inscription est deja a
     * jour quand elle part. Sa ligne `{nature}` est la seule qui dise s'il y a de l'argent a rendre,
     * et elle le dit en toutes lettres plutot que par un mot a interpreter.
     */
    readonly annulation: ModeleDEmail;
  };
};

/** Un email de pilotage : son objet et son corps, tous deux recopies du document. */
export type ModeleDEmail = {
  readonly objet: string;
  readonly corps: string;
};

/**
 * Un email dont un paragraphe varie, et dont le document ecrit les DEUX versions.
 *
 * IL N'EN EXISTE QUE DEUX, ET AUCUNE NE SE DEDUIT DE L'AUTRE : en composer une troisieme ici serait
 * ecrire un texte que la personne verrait sans qu'aucune spec le porte. C'est la forme que
 * `confirmation`, l'email 1, emploie deja depuis le 2026-08-15.
 */
export type ModeleDEmailADeuxVersions = ModeleDEmail & {
  /** Version A, quand le virement est deja recu. */
  readonly paragrapheVirementRecu: string;
  /** Version B, quand le virement n'est pas encore recu. */
  readonly paragrapheVirementAttendu: string;
};

/**
 * Un modele dont le paragraphe variable depend du SOLDE, et non d'un virement recu ou non.
 *
 * DEUX MODELES LE PORTENT DEPUIS LE 2026-08-19 : l'email 4 et celui du virement suivant. Ils ne
 * partagent pas le type de `sessionConfirmee`, dont les deux versions repondent a une autre
 * question : « cette personne a-t-elle deja vire ». Ici la question est « que doit-elle encore ».
 *
 * `paragrapheSansSolde` EST FACULTATIF, et un seul des deux le porte. L'email 4 confond deux cas dans
 * sa version A, solde nul et solde inconnu : dans les deux, la seule phrase honnete est qu'il n'y a
 * rien de plus a demander. L'email du virement suivant les distingue, parce qu'il ne peut pas dire
 * « il n'y a plus rien a regler » a une inscription dont personne ne sait ce qu'elle doit.
 */
export type ModeleDEmailAuSolde = ModeleDEmail & {
  /** Version A, quand l'inscription est soldee. */
  readonly paragrapheSoldee: string;
  /** Version B, quand il reste quelque chose a virer. `{solde}` y est remplace. */
  readonly paragrapheAvecSolde: string;
  /** Version C, quand le solde ne se calcule pas : ni prix sur la session, ni regime. */
  readonly paragrapheSansSolde?: string;
};

/**
 * Le texte du parcours d'inscription, recopie de `contenu-site.md` v4 § Le parcours d'inscription.
 *
 * IL NE VIT PAS SOUS `sections` : ce texte ne s'affiche pas sur la page d'accueil, mais sur la page
 * `/inscription`, qui n'est pas une section de `contenu-site.md` et n'entre dans aucun des comptes
 * de `AC-CNT-001` ni de `AC-SIT-010`. Le poser sous `sections` aurait fait compter une
 * vingt-deuxieme section a deux controles qui enumerent ce qui s'affiche sur `/`.
 *
 * IL N'EST PAS SOUS `redactionDev` NON PLUS, bien que le dev l'ait ecrit : ce qu'il a ecrit est
 * DANS LA SPEC, sur la delegation de Wahid du 2026-08-15, et il se recopie d'ici comme le reste.
 * `redactionDev` ne porte que des chaines qui n'existent dans AUCUN document opposable.
 *
 * ETAT AU 2026-08-15, TACHE 07-07 : LES CINQ ETAPES SONT RECOPIEES. La tache 07-06 n'en portait que
 * trois, les etapes 4 et 5 n'ayant alors aucun ecran ; recopier un texte que rien n'affiche aurait
 * donne a croire que l'ecran existait.
 */
export type ParcoursDInscription = {
  readonly titre: string;
  readonly sousTitre: string;
  /** Les cinq reperes du fil des etapes, dans l'ordre du document. */
  readonly fil: readonly string[];
  readonly etapes: {
    readonly laSession: {
      readonly titre: string;
      readonly aide: string;
      readonly filtre: {
        readonly intitule: string;
        readonly toutesLesVilles: string;
      };
      /** Le nombre de places restantes, dans les trois cas que la spec enumere. */
      readonly places: {
        readonly plusieurs: string;
        readonly une: string;
        readonly complet: string;
      };
      readonly sansDate: { readonly libelle: string; readonly aide: string };
      readonly aucuneOuverte: string;
      readonly bouton: string;
      /**
       * Les quatre intitules qui precedent les valeurs d'une session, ecrits en v16 le 2026-08-19.
       *
       * L'ETAPE 1 ET LE RECAPITULATIF DE L'ETAPE 3 LISENT LES MEMES : `contenu-site.md` v16 § Etape 3
       * ecrit « Le bloc de la session reprend les quatre intitules du § Etape 1 ». Deux jeux
       * d'intitules auraient permis a un ecran de dire « Lieu » et a l'autre « Adresse ».
       */
      readonly intitules: {
        readonly date: string;
        readonly ville: string;
        readonly horaire: string;
        readonly lieu: string;
      };
    };
    readonly vosCoordonnees: {
      readonly titre: string;
      /*
       * ⚠️ PAS DE CLE `aide`, ET C'EST UN RETRAIT DU 2026-08-20, `contenu-site.md` v18. Elle portait
       * « Elles me servent a vous ecrire quand la session se confirme. Rien d'autre. », et Wahid a
       * demande de l'enlever. Les quatre autres etapes gardent la leur.
       */
      readonly champs: readonly ChampFormulaire[];
      readonly disponibilites: {
        readonly intitule: string;
        readonly aide: string;
        readonly quand: GroupeDeCases;
        readonly quelJour: GroupeDeCases;
      };
      readonly remarque: { readonly intitule: string; readonly aide: string };
      /**
       * Ce que la liste des villes affiche tant qu'aucune n'est choisie.
       *
       * ⚠️ CETTE CHAINE EST ECRITE PAR LE DEV, LE 2026-08-20, et elle n'est pas dans
       * `contenu-site.md`. Wahid a demande que la ville de preference devienne une liste
       * deroulante ; une liste deroulante a une premiere entree, et le document ne l'ecrit pas.
       * La demande et cette consequence sont inscrites dans `.documentation/REMONTEES.md`.
       */
      readonly villeEntreeVide: string;
      /**
       * La mention de consentement, qui a remplace la case a cocher le 2026-08-19.
       *
       * ⚠️ CE N'EST PLUS UN LIBELLE DE CHAMP, C'EST UNE PHRASE AFFICHEE. Le champ 7 a quitte le
       * tableau de `contenu-site.md` v16 § Etape 2, et avec lui le message d'erreur qui disait de
       * cocher. Le consentement se donne en continuant, et la colonne de la base recoit `vrai`.
       */
      readonly mentionDeConsentement: string;
      readonly bouton: string;
      readonly lienRetour: string;
    };
    readonly votreRelecture: {
      readonly titre: string;
      readonly aide: string;
      readonly blocs: readonly {
        readonly intitule: string;
        readonly lien: string;
      }[];
      readonly mentionSansDate: string;
      readonly bouton: string;
      readonly lienRetour: string;
    };
    /**
     * L'etape 4, le virement.
     *
     * ⚠️ LA CLE `coordonnees` A DISPARU LE 2026-08-16, ELLE NE S'EST PAS VIDEE. Elle portait les
     * quatre mentions `[a completer : ...]` du tableau bancaire de `contenu-site.md` § Etape 4 :
     * titulaire du compte, banque, RIB ou IBAN, montant. Wahid a tranche le 2026-08-16 qu'il
     * communique le RIB et le montant PAR TELEPHONE, a chaque personne. `contenu-site.md` v7 retire
     * le tableau et pose `lAppel` a sa place. Une cle laissee avec un tableau vide aurait laisse un
     * composant qui rend une liste vide, et le lecteur suivant n'aurait pas su si c'etait voulu.
     *
     * IL N'Y A NI CHAMP DE CARTE, NI BOUTON DE PAIEMENT, et ce n'est pas un oubli : Wahid a tranche
     * le 2026-08-15 qu'aucun paiement en ligne n'existe. `AC-INS-004` cherche un champ de carte et
     * un appel vers un prestataire, et exige de n'en trouver aucun.
     */
    readonly leVirement: {
      readonly titre: string;
      /** Les trois paragraphes du corps, dans l'ordre du document. */
      readonly corps: readonly string[];
      readonly ceQuiSuit: string;
      readonly bouton: string;
    };
    /**
     * L'etape 5, la confirmation.
     *
     * SON CORPS A DEUX VERSIONS, et la spec les ecrit toutes les deux : celle de qui a retenu une
     * session, ou `{date}` et `{ville}` se remplacent, et celle de qui n'en a retenu aucune. Ecrire
     * un seul texte qui couvrirait les deux cas aurait demande une phrase que le document n'a pas.
     */
    readonly cEstEnregistre: {
      readonly titre: string;
      /** Deux paragraphes, le premier portant `{date}` et `{ville}`. */
      readonly corpsAvecSession: readonly string[];
      /** Deux paragraphes, sans marqueur : aucune date n'a ete retenue. */
      readonly corpsSansSession: readonly string[];
      readonly sousLeCorps: string;
      readonly lien: string;
    };
  };
  /** Les six messages d'erreur du parcours, dans l'ordre du tableau de la spec. */
  readonly erreurs: {
    readonly champObligatoireVide: string;
    readonly emailMalForme: string;
    readonly boutonEnvoiEnCours: string;
    readonly aucuneSessionChoisie: string;
    readonly sessionComplete: string;
    readonly sessionFermee: string;
    /**
     * Le message du doublon, ne le 2026-08-20, `contenu-site.md` v21 § Les messages d'erreur du
     * parcours.
     *
     * SA SECONDE PHRASE EST RECOPIEE DU § Etape 5, mot pour mot : « ecrivez-moi a
     * niya@wahidlahlou.com, ou sur WhatsApp au +212 660 617 677 ». C'est ce que Wahid a demande le
     * meme jour, « alerter l'utilisateur et lui demander de communiquer sur mon tel ou par email ».
     */
    readonly dejaInscrit: string;
  };
  readonly echecTechnique: string;
};

/**
 * Le texte du formulaire de contact, recopie de `contenu-site.md` v4 § Le formulaire de contact.
 *
 * IL NE VIT PAS SOUS `sections`, POUR LE MEME MOTIF QUE `inscription` : ce texte s'affiche sur la
 * page `/contact`, qui n'est pas une section de `contenu-site.md` et n'entre ni dans le compte de
 * `AC-CNT-001` ni dans celui de `AC-SIT-010`.
 *
 * IL NE PORTE NI LE LIBELLE DU LIEN QUI MENE ICI DEPUIS LA PAGE D'ACCUEIL, NI LE TEXTE DE LA
 * NOTIFICATION ENVOYEE A L'ADMINISTRATEUR. Aucun des deux n'existe dans un document opposable :
 * ce sont `Q-018` et `Q-019` de `.documentation/REMONTEES.md`, toutes deux non bloquantes, et leurs
 * interims n'ecrivent aucun mot nouveau. Le lien reprend l'intitule « Contact » du pied de page,
 * section 23 ; la notification compose son objet et ses intitules avec les chaines de ce bloc-ci.
 *
 * `AC-CTC-005` ENUMERE CETTE CLE ET COMPARE CHAQUE CHAINE AU DOCUMENT. Toute chaine ajoutee ici
 * entre dans la comparaison sans qu'une ligne du controle bouge, et une chaine inventee en sort
 * comme absente du document.
 */
export type FormulaireDeContact = {
  readonly titre: string;
  readonly sousTitre: string;
  /** Les quatre champs, dans l'ordre du tableau du document. */
  readonly champs: readonly ChampFormulaire[];
  /** L'aide sous le champ du message. */
  readonly aideDuMessage: string;
  readonly bouton: string;
  readonly messages: {
    /** Ce qui s'affiche apres envoi. */
    readonly confirmation: string;
    /** Ce qui s'affiche en cas d'echec technique. */
    readonly echec: string;
  };
  /** Le libelle du lien de retour vers la page de la journee. */
  readonly lien: string;
};

/**
 * Une rubrique d'une page annexe : son intitule affiche, et ses paragraphes.
 *
 * L'INTITULE S'AFFICHE, ET C'EST L'INVERSE DE LA REGLE DES SECTIONS 1 A 21. Dans celles-la, un
 * intitule `###` du document nomme le ROLE du texte qui suit, « Titre », « Corps », « Visuel », et
 * l'afficher mettrait a l'ecran un mot de redaction interne : c'est la decision du 2026-08-16 sur la
 * question 014. Sur les deux pages annexes, les intitules SONT le texte : « L'editeur du site »,
 * « Vos droits » sont les rubriques d'une page de mentions legales, et les enchainer sans eux
 * donnerait une page illisible. `contenu-site.md` v11 ecrit cette regle et ses deux exceptions.
 */
export type RubriqueAnnexe = {
  readonly intitule: string;
  readonly paragraphes: readonly string[];
};

/** Une page annexe : son titre, ses rubriques, et le libelle de son lien de retour. */
export type PageAnnexe = {
  readonly titre: string;
  readonly rubriques: readonly RubriqueAnnexe[];
  readonly lien: string;
};

/**
 * Les deux pages annexes, `RM-ANX-001`.
 *
 * LE TEXTE EST RECOPIE DE `contenu-site.md` v11 § Les mentions legales et § La politique de
 * confidentialite, ecrits par le dev le 2026-08-17 sur deux delegations explicites de Wahid : « Pour
 * les mentions legales, effectue une recherche sur Internet et redige-les », et « Le texte doit etre
 * generique et dire que les donnees ne sont gardees que pour l'usage logistique ».
 *
 * LES QUATRE VALEURS LEGALES VIENNENT DE WAHID, le 2026-08-17, et aucune ne s'invente : la raison
 * sociale, l'adresse, l'ICE, et sa reponse « pas de recepisse CNDP (pas besoin) », qui a fait retirer
 * la mention correspondante des deux pages.
 */
/**
 * La page 404, troisieme page annexe de `RM-ANX-001`.
 *
 * ELLE N'A PAS DE RUBRIQUES, ET C'EST POURQUOI ELLE N'EST PAS UNE `PageAnnexe`. Les deux autres
 * pages annexes sont des documents : un titre, des rubriques `h2`, des paragraphes. Celle-ci porte
 * quatre elements et pas un de plus, et un type qui les nomme vaut mieux qu'un tableau de rubriques
 * a une seule entree.
 *
 * LE NOM DU PRODUIT N'Y EST PAS : il vit sous `redactionDev.navigation.nomProduit`, ou il sert deja
 * la barre de navigation, et le rendu le lit la-bas. Le recopier ici en ferait deux sources pour un
 * meme mot, et rien ne garantirait qu'elles disent la meme chose.
 */
export type PageIntrouvable = {
  readonly titre: string;
  readonly message: string;
  readonly lien: string;
};

export type PagesAnnexes = {
  readonly mentionsLegales: PageAnnexe;
  readonly confidentialite: PageAnnexe;
  readonly introuvable: PageIntrouvable;
};

/**
 * Le texte de la page d'annulation, recopie de `contenu-site.md` v21 § La page d'annulation.
 *
 * ⚠️ LES TROIS PALIERS QU'ELLE AFFICHE NE SONT PAS INVENTES ICI. Ils sont ceux du § La FAQ, question
 * « Que se passe-t-il si je ne peux plus venir ? », publiee sur le site depuis la v1 : remboursement
 * integral jusqu'a 14 jours avant la date annoncee, report de la place entre 14 et 7 jours, et
 * transmission de la place a un collegue a tout moment. La page APPLIQUE cette phrase ; les deux
 * bornes vivent dans `lib/annulation.ts`, et `AC-ANN-004` a `AC-ANN-006` les mesurent.
 */
export type PageDAnnulation = {
  readonly titre: string;
  readonly sousTitre: string;
  /** Les quatre intitules du rappel de la session. */
  readonly rappel: {
    readonly titre: string;
    readonly jour: string;
    readonly ville: string;
    readonly lieu: string;
    /** Ce qui remplace la date quand aucune session n'est retenue. */
    readonly sansDate: string;
  };
  /**
   * Les quatre etats, et ce que chacun propose.
   *
   * ⚠️ `tropTard` N'A PAS DE BOUTON, et c'est le seul des quatre dans ce cas. Une chaine vide y
   * serait un bouton sans libelle ; la cle est donc absente du type, et la page ne rend aucun
   * formulaire dans cet etat. `AC-ANN-006` mesure que la page n'offre rien ET qu'une demande postee
   * malgre tout est refusee sans ecriture.
   */
  readonly etats: {
    readonly sansVirement: { readonly texte: string; readonly bouton: string };
    readonly remboursement: { readonly texte: string; readonly bouton: string };
    readonly report: { readonly texte: string; readonly bouton: string };
    readonly tropTard: { readonly texte: string };
  };
  /** Ce qui s'affiche apres la confirmation, ou quand il n'y a rien a confirmer. */
  readonly apres: {
    readonly sansVirement: string;
    readonly remboursement: string;
    readonly report: string;
    readonly dejaAnnulee: string;
    readonly lienInconnu: string;
    readonly echec: string;
  };
};

export type Contenu = {
  readonly sections: Sections;
  readonly inscription: ParcoursDInscription;
  readonly contact: FormulaireDeContact;
  readonly pagesAnnexes: PagesAnnexes;
  readonly interface: LibellesInterface;
  readonly visuels: Visuels;
  readonly redactionDev: RedactionDev;
  readonly annulation: PageDAnnulation;
  readonly emails: Emails;
};

const emails: Emails = {
  /*
   * LE CORPS EST RECOPIE LIGNE A LIGNE DE LA CITATION DE `pilotage-inscriptions.md` v4 § 1. Une
   * ligne vide du document est une ligne vide ici, et l'ordre des lignes est le sien. Les deux
   * mentions `[a completer : ...]` sont celles du document, et elles ne se comblent pas.
   */
  confirmation: {
    objet: "Votre demande de place est bien arrivée",
    corps:
      "Bonjour {prenom},\n" +
      "\n" +
      "Votre demande de place pour la journée « Construisez votre Harness IA » est bien arrivée.\n" +
      "\n" +
      "{paragraphe}\n" +
      "\n" +
      "Je vous envoie les coordonnées bancaires et le montant à virer par email.\n" +
      "La référence à indiquer : votre prénom et votre nom.\n" +
      "\n" +
      "Vous ne pouvez plus venir ? Vous annulez votre inscription ici : {lienAnnulation}\n" +
      "\n" +
      "Une question d'ici là ? Répondez à cet email, ou écrivez-moi sur WhatsApp au +212 660 617 677.\n" +
      "\n" +
      "Wahid Lahlou\n" +
      "Niya · Casablanca",
    /*
     * LA VERSION A PORTE LA SESSION SUR QUATRE LIGNES DEPUIS LE 2026-08-19, `pilotage-inscriptions.md`
     * v11. Elle l'ecrivait en une phrase et NE DISAIT PAS LE LIEU : la personne recevait la date et la
     * ville, et devait attendre un autre email pour savoir ou aller. Demande de Wahid du meme jour,
     * « l'email doit rappeler le lieu aussi, meme format que l'affichage de la session ».
     *
     * LES QUATRE INTITULES SONT CEUX DE L'ECRAN, et `{horaire}` est un marqueur nouveau : les heures
     * viennent des colonnes de la session, comme a l'etape 1, et non d'un « de 9 h 00 a 17 h 00 »
     * fige qui mentirait sur une session ouverte a 10 h 00.
     */
    paragrapheAvecSession:
      "Vous avez retenu la session suivante :\n" +
      "\n" +
      "Date : {date}\n" +
      "Ville : {ville}\n" +
      "Horaire : {horaire}\n" +
      "Lieu : {lieu}\n" +
      "\n" +
      "Votre virement réserve votre place, et les places se comptent à réception des paiements. " +
      "Dès qu'il arrive, je vous le confirme et je vous envoie le livret de bienvenue.",
    paragrapheSansSession: {
      disponibilites:
        "Vous n'avez retenu aucune date pour le moment, et vous avez indiqué être disponible : " +
        "{disponibilites}. J'en tiens compte pour ouvrir la prochaine session. ",
      suite:
        "Je réunis 12 à 15 personnes par session : dès qu'une date se fixe, je vous écris avec le lieu, " +
        "et votre virement réserve alors votre place.",
    },
  },
  notification: {
    objet: "Nouvelle demande de place · {nom} · {total} au total",
    champs: {
      prenomNom: "Prénom et nom",
      email: "Email",
      telephone: "Téléphone",
      // « Ville de préférence » DEPUIS LE 2026-08-20 : la ligne ne part que pour une demande sans
      // session retenue, et la ville d'une demande AVEC session est celle de la ligne « Session
      // retenue » juste dessous. Ecrire « Ville » sur les deux aurait dit deux choses differentes.
      ville: "Ville de préférence",
      session: "Session retenue",
      disponibilites: "Disponibilités",
      remarque: "Remarque",
      consentement: "Consentement",
      source: "Source",
    },
    sessionAucune: "Aucune de ces dates ne me convient",
    pied: "Demandes à l'étape « Demande reçue » : {total}",
  },

  /*
   * LES QUATRE MODELES SONT RECOPIES LIGNE A LIGNE DES CITATIONS DE `pilotage-inscriptions.md` v5.
   * Une ligne vide du document est une ligne vide ici, et l'ordre des lignes est le sien.
   * `AC-ADM-044` relit le document et compare : une virgule deplacee fait rougir.
   */
  pilotage: {
    /*
     * ⚠️ CET EMAIL EST L'EMAIL DE PAIEMENT DEPUIS LE 2026-08-19, `pilotage-inscriptions.md` v13. Il
     * annoncait « Je vous envoie les coordonnees bancaires et le montant a virer par email », phrase
     * posee le matin du meme jour, ET IL NE LES DONNAIT PAS. Il les donne.
     *
     * IL REFUSE DE PARTIR s'il manque le regime, le prix de la session ou le compte affecte :
     * `{montant}` ou `{compte}` seraient vides, et `remplace` rend alors `null`. Un email annoncant
     * « Le montant a virer : . » ne se rattrape pas.
     */
    acceptation: {
      objet: "Votre place est retenue pour la session du {date}",
      corps:
        "Bonjour {Prénom},\n" +
        "\n" +
        "Votre demande de place pour la journée « Construisez votre Harness IA » est retenue pour la session du {date} à {ville}, de 9 h 00 à 17 h 00.\n" +
        "\n" +
        "Ce qui reste à faire de votre côté : le virement. C'est lui qui réserve votre place, et les places se comptent à réception des paiements.\n" +
        "\n" +
        "Le montant à virer : {montant}.\n" +
        "La référence à indiquer : votre prénom et votre nom.\n" +
        "\n" +
        "Les coordonnées du compte :\n" +
        "{compte}\n" +
        "\n" +
        "Vous ne pouvez plus venir ? Vous annulez votre inscription ici : {lienAnnulation}\n" +
        "\n" +
        "Une question ? Répondez à cet email, ou écrivez-moi sur WhatsApp au +212 660 617 677.\n" +
        "\n" +
        "Wahid Lahlou\n" +
        "Niya · Casablanca",
    },
    refus: {
      objet: "Votre demande de place pour la session du {date}",
      corps:
        "Bonjour {Prénom},\n" +
        "\n" +
        "Je ne peux pas retenir votre demande pour la session du {date} à {ville}.\n" +
        "\n" +
        "Votre demande reste enregistrée. Dès qu'une prochaine session s'ouvre, je vous écris pour vous proposer une place, et vous serez parmi les premiers prévenus.\n" +
        "\n" +
        "Si vous voulez en parler, répondez à cet email ou écrivez-moi sur WhatsApp au +212 660 617 677.\n" +
        "\n" +
        "Wahid Lahlou\n" +
        "Niya · Casablanca",
    },
    annulation: {
      objet: "La session du {date} est annulée",
      corps:
        "Bonjour {Prénom},\n" +
        "\n" +
        "La session du {date} à {ville} n'aura pas lieu.\n" +
        "\n" +
        "Votre inscription reste enregistrée et vous n'avez rien à refaire. Dès que la prochaine date se fixe, je vous écris en premier avec le lieu et l'horaire.\n" +
        "\n" +
        "Si vous avez déjà fait le virement, écrivez-moi et nous voyons ensemble : soit je porte votre place sur la session suivante, soit je vous rembourse.\n" +
        "\n" +
        "Répondez à cet email, ou écrivez-moi sur WhatsApp au +212 660 617 677.\n" +
        "\n" +
        "Wahid Lahlou\n" +
        "Niya · Casablanca",
    },
    /*
     * ⚠️ LE CORPS PORTE `{paragraphe}` DEPUIS LA v6, ET LES DEUX VERSIONS SONT SOUS LUI. La v5
     * ecrivait la version B en dur au milieu du corps, ce qui revenait a demander un virement a une
     * personne qui avait deja paye : c'est ce que la tache 08-04 corrige, sur `AC-PAY-025`.
     *
     * `{paragraphe}` EST LE MEME MARQUEUR QUE CELUI DE L'EMAIL 1, et non celui du document, qui
     * ecrit `{paragraphe variable, voir ci-dessous}`. Les deux autres marqueurs, `{date}` et
     * `{ville}`, restent ceux du document : eux sont remplaces par `AC-ADM-044` des DEUX cotes de sa
     * comparaison, tandis que `{paragraphe}` disparait avant l'envoi et n'a rien a comparer.
     */
    sessionConfirmee: {
      objet: "La session est confirmée : {date}, à {ville}",
      corps:
        "Bonjour {Prénom},\n" +
        "\n" +
        "La session du {date} est confirmée. Elle a lieu à {ville}, {lieu}, de 9 h 00 à 17 h 00.\n" +
        "\n" +
        "{paragraphe}\n" +
        "\n" +
        "À prévoir avant de venir : un abonnement Claude Pro ou Max, actif le jour de la journée.\n" +
        "\n" +
        "Une question ? Répondez à cet email, ou écrivez-moi sur WhatsApp au +212 660 617 677.\n" +
        "\n" +
        "Wahid Lahlou\n" +
        "Niya · Casablanca",
      paragrapheVirementRecu:
        "Votre virement est arrivé et votre place est réservée. De votre côté, tout est en ordre.",
      paragrapheVirementAttendu:
        "Votre virement réserve votre place, et les places se comptent à réception des paiements.\n" +
        "\n" +
        "Je vous envoie les coordonnées bancaires et le montant à virer par email.\n" +
        "La référence à indiquer : votre prénom et votre nom.",
    },
    /*
     * LES DEUX MODELES DU DESSOUS SONT RECOPIES LIGNE A LIGNE DE `pilotage-inscriptions.md` **v6**,
     * tache 08-03. `AC-PAY-015` relit le document et compare la chaine REELLEMENT transmise par le
     * relais : une virgule deplacee, une ligne perdue ou une reformulation font echouer.
     *
     * ⚠️ LA MENTION `[à compléter : ...]` DE L'EMAIL 4 EST DANS LE DOCUMENT, ET ELLE NE SE COMBLE
     * PAS. Le livret de bienvenue n'existe dans aucun fichier du depot, et une piece jointe ne
     * s'invente pas plus qu'une phrase. Elle est VISIBLE par la personne qui recoit l'email, ce qui
     * est exactement son role : elle dit ce qui manque plutot que de le faire disparaitre.
     */
    placeReservee: {
      objet: "Votre place est réservée",
      corps:
        "Bonjour {Prénom},\n" +
        "\n" +
        "Votre virement est bien arrivé, et votre place pour la journée « Construisez votre Harness IA » est réservée.\n" +
        "\n" +
        "Rendez-vous le {date} à {ville}, {lieu}, de 9 h 00 à 17 h 00.\n" +
        "\n" +
        "{paragraphe}\n" +
        "\n" +
        "Le livret de bienvenue : [à compléter : le livret de bienvenue, à joindre à cet email]\n" +
        "\n" +
        "Deux choses à prévoir avant de venir. Un abonnement Claude Pro ou Max, actif le jour de la journée : c'est le seul prérequis, et il est à souscrire avant de venir. Votre matière de travail, si vous le souhaitez : une présentation de vous, une page sur votre activité, un projet en cours, et quinze à trente fichiers en désordre autour de ce projet. La journée reste entièrement suivable sans rien apporter.\n" +
        "\n" +
        "Vous ne pouvez plus venir ? Vous annulez votre inscription ici : {lienAnnulation}\n" +
        "\n" +
        "Une question d'ici là ? Répondez à cet email, ou écrivez-moi sur WhatsApp au +212 660 617 677.\n" +
        "\n" +
        "Wahid Lahlou\n" +
        "Niya · Casablanca",
      /*
       * LES DEUX VERSIONS DU PARAGRAPHE, `pilotage-inscriptions.md` v13 § 4.
       *
       * LA VERSION A COUVRE DEUX CAS ET NE LES DISTINGUE PAS : l'inscription est soldee, ou le solde
       * ne se calcule pas faute de prix ou de regime. Dans les deux, la seule phrase honnete est
       * qu'il n'y a rien de plus a demander : reclamer un solde inconnu serait pire que se taire.
       */
      paragrapheSoldee:
        "Votre inscription est soldée. Il n'y a plus rien à régler.",
      paragrapheAvecSolde:
        "Ce virement de {versement} est enregistré. Il reste {solde} à virer avant la journée, avec la même référence.",
    },
    /*
     * L'EMAIL DU VIREMENT SUIVANT, NE LE 2026-08-19, `pilotage-inscriptions.md` v13.
     *
     * IL PART A CHAQUE VIREMENT QUI N'EST PAS LE PREMIER de son inscription, et l'email 4 ne part
     * plus dans ce cas : dire « votre place est reservee » a qui la tient depuis un mois n'apprend
     * rien, et cache ce que l'email doit dire.
     *
     * ⚠️ DEUX MARQUEURS ET NON UN SEUL : `{versement}` est le montant du virement qui vient
     * d'arriver, `{montant}` est ce que l'inscription doit. Un marqueur unique aurait change de sens
     * d'une section a l'autre du document.
     */
    virementSuivant: {
      objet: "J'ai bien reçu votre virement",
      corps:
        "Bonjour {Prénom},\n" +
        "\n" +
        "Votre virement de {versement} est bien arrivé, pour la journée du {date} à {ville}.\n" +
        "\n" +
        "{paragraphe}\n" +
        "\n" +
        "Une question d'ici là ? Répondez à cet email, ou écrivez-moi sur WhatsApp au +212 660 617 677.\n" +
        "\n" +
        "Wahid Lahlou\n" +
        "Niya · Casablanca",
      paragrapheSoldee:
        "Votre inscription est soldée. Il n'y a plus rien à régler, et votre place reste tenue.",
      paragrapheAvecSolde:
        "Il reste {solde} à virer avant la journée, avec la même référence. Votre place reste tenue.",
      paragrapheSansSolde: "Votre place reste tenue.",
    },
    annulationFauteDePlace: {
      objet: "Votre inscription pour la session du {date}",
      corps:
        "Bonjour {Prénom},\n" +
        "\n" +
        "Les places de la session du {date} à {ville} sont toutes prises. Les virements arrivés avant le vôtre les ont réservées, et je ne peux pas retenir votre inscription pour cette date.\n" +
        "\n" +
        "Les places se comptent à réception des paiements, dans l'ordre où ils arrivent : votre inscription était en règle, et c'est le rang des virements qui a tranché.\n" +
        "\n" +
        "Votre inscription reste enregistrée. Dès que la prochaine date se fixe, je vous écris en premier avec le lieu et l'horaire, et vous serez parmi les premiers prévenus.\n" +
        "\n" +
        "Si vous avez fait le virement entre-temps, écrivez-moi : nous voyons ensemble, soit je porte votre place sur la session suivante, soit je vous rembourse.\n" +
        "\n" +
        "Répondez à cet email, ou écrivez-moi sur WhatsApp au +212 660 617 677.\n" +
        "\n" +
        "Wahid Lahlou\n" +
        "Niya · Casablanca",
    },
  },

  /*
   * LES DEUX ALERTES SONT RECOPIEES LIGNE A LIGNE DE `pilotage-inscriptions.md` **v6**, § Le minimum
   * est atteint et § Le préavis, tache 08-04.
   *
   * AUCUNE DES DEUX NE SALUE PERSONNE, et ce n'est pas un oubli de recopie : elles vont a Wahid, qui
   * est seul a les lire. Le document ne leur donne pas de « Bonjour {Prénom} », et en ajouter un
   * serait ecrire une phrase qu'aucune spec ne porte.
   */
  alertes: {
    /*
     * L'ALERTE D'UNE ANNULATION PAR L'INSCRIT, `pilotage-inscriptions.md` v14 § Une annulation par
     * l'inscrit. Elle est posee en premier parce qu'elle est la plus recente ; l'ordre des cles n'a
     * aucun effet, `AC-ADM-044` comparant chaque chaine a son paragraphe du document.
     */
    annulation: {
      objet: "Annulation : {nom} · {session}",
      corps:
        "{nom} vient d'annuler son inscription.\n" +
        "\n" +
        "Session : {session}\n" +
        "Ce qui a été fait : {nature}\n" +
        "Coordonnées : {coordonnees}\n" +
        "\n" +
        "Cet email prévient. L'inscription est déjà à jour dans l'administration, et il n'y a rien à y faire, sauf le remboursement quand il est annoncé ci-dessus.",
    },
    minimumAtteint: {
      objet: "Le minimum est atteint : {date}, à {ville}",
      corps:
        "La session du {date} à {ville} porte {payes} places payées, sur {places} places et un minimum de {minimum}.\n" +
        "\n" +
        "Elle est confirmable. Rien n'est confirmé pour autant : la confirmation reste un geste à faire, dans la vue Sessions du tableau de bord.\n" +
        "\n" +
        "Cet email ne part qu'une fois pour cette session. Les virements suivants ne le renvoient pas.",
    },
    preavis: {
      objet:
        "Préavis salle et traiteur : {date}, à {ville}, dans {jours} jours",
      corps:
        "La session du {date} à {ville} a lieu dans {jours} jours, et c'est le préavis inscrit sur elle pour la salle et le traiteur.\n" +
        "\n" +
        "Elle porte {payes} places payées, sur {places} places et un minimum de {minimum}.\n" +
        "\n" +
        "Cet email prévient. Il ne confirme rien, il n'annule rien, et il ne bloque aucune décision.",
    },
  },
};

const sections: Sections = {
  // 1. Haut de page
  hautDePage: {
    titre:
      "Découvrez le Harness et révolutionnez votre manière de travailler grâce à l'IA, en une journée de formation.",
    sousTitre: [
      "Tous les modèles d'IA se valent. Ce qui change tout, c'est le ",
      { gras: "système" },
      " que vous mettez autour. Une journée en présentiel à Casablanca, 12 à 15 personnes, sur ",
      { gras: "Claude Cowork" },
      ", pour monter le vôtre sur vos vrais dossiers, avec une ",
      { gras: "méthodologie" },
      " éprouvée sur plus de deux ans.",
    ],
    bouton: "Je réserve ma place",
    sousLeBouton:
      "4 800 DH HT par personne, déjeuner et pauses compris. Les dates s'ouvrent au fil des inscriptions.",
    visuels: ["[[PH-01]]"],
  },

  // 2. Qu'est-ce que le Harness ?
  quEstCeQueLeHarness: {
    titre: "Qu'est-ce que le Harness ?",
    corps: [
      [
        "Le ",
        { gras: "Harness" },
        " est le système que vous construisez autour de l'IA : le harnais que vous mettez autour d'elle pour que ",
        "toute sa puissance aille dans votre direction",
        ". Il tient en trois pièces, et elles vivent dans vos fichiers : ",
        { gras: "une mémoire" },
        " de vos projets et de vos décisions, ",
        "une architecture de suivi",
        " que l'IA sait parcourir, et ",
        "vos règles de travail",
        " écrites une fois pour toutes.",
      ],
      [
        "Le monter demande de ",
        { gras: "changer de mindset" },
        ", et c'est la bascule que cette journée vous fait franchir.",
      ],
      [
        "Des applications comme ",
        { gras: "Claude Cowork" },
        " rendent tout cela possible : elles donnent à l'IA ",
        "l'accès à vos fichiers et les moyens d'agir dessus",
        ".",
      ],
    ],
  },

  // 3. Bandeau des sept valeurs
  bandeauDesValeurs: {
    titre: "Ce que vous emportez de cette journée",
    items: [
      {
        intitule: "Le changement de mindset.",
        texte:
          "Utiliser l'IA de façon fiable l'exige. Cette journée vous le fait franchir : vous passez de consommateur d'IA à architecte de votre IA.",
      },
      {
        intitule: "Votre système, le Harness.",
        texte: "Construit pendant la journée, sur vos propres projets.",
      },
      {
        intitule: "La compréhension de la machine.",
        texte: "Vous saurez d'où viennent ses défauts, et quoi corriger.",
      },
      {
        intitule: "Une méthodologie actionnable.",
        texte:
          "La boucle de rétroaction qui juge le travail produit, éprouvée sur plus de deux ans.",
      },
      {
        intitule: "Des techniques concrètes.",
        texte: "Des gestes réutilisables, toujours au service d'un objectif.",
      },
      {
        intitule: "Une productivité multipliée.",
        texte: "Sur vos vrais dossiers, dès la première semaine.",
      },
      {
        intitule: "Votre autonomie.",
        texte:
          "Vous refaites tout seul, sur ce projet ou sur n'importe quel autre.",
      },
    ],
  },

  // La section « Preuve sociale » de la v1 a ete retiree : elle n'a jamais eu de cle ici

  // 4. Le formateur
  leFormateur: {
    titre: "Wahid Lahlou",
    corpsAvantLaListe: [
      "Je suis entrepreneur tech. Depuis plus de vingt-cinq ans, je fonde des entreprises et je les dirige, avec une double formation en génie informatique et en administration des affaires. J'ai été CEO de chacune d'elles.",
      "Aujourd'hui, je mène trois activités :",
    ],
    activites: [
      [
        { gras: "CEO d'une startup InsurTech" },
        ", en phase de mise en production.",
      ],
      [{ gras: "Cofondateur et directeur technique d'une plateforme SaaS." }],
      [
        { gras: "Consultant en IA et en technologie" },
        " auprès d'entreprises.",
      ],
    ],
    corpsApresLaListe: [
      [
        "Ces trois activités tournent sur ",
        "la méthode de cette journée",
        ". Je la pratique et je l'affine ",
        "depuis plus de deux ans",
        ", sur mes propres projets, et je la transmets à ",
        "des dirigeants, des entrepreneurs et des équipes de TPE et PME",
        ", en sessions individuelles et en formations de groupe.",
      ],
      [
        "Transmettre fait partie de mon métier depuis longtemps : pendant ",
        { gras: "treize ans" },
        ", ma précédente société a accompagné ",
        "plus de 220 entreprises marocaines",
        " dans leur passage au cloud, en formant leurs administrateurs et leurs utilisateurs.",
      ],
      [
        "Un point qui compte pour cette journée : ",
        "je travaille devant vous sur mes propres projets réels",
        ". La méthode que vous verrez tourner est ",
        "celle qui fait tourner mon activité",
        ".",
      ],
    ],
    lien: {
      libelle: "Voir le profil LinkedIn",
      href: "https://www.linkedin.com/in/wahidlahlou",
    },
    visuels: ["[[PH-03]]"],
  },

  // 5. Le changement de mindset
  leChangementDeMindset: {
    titre: "Vous cesserez de chercher la bonne phrase.",
    corps: [
      [
        "Tant que vous voyez l'IA comme une boîte où l'on pose des questions, vous en tirerez ce qu'une boîte à questions peut donner : des réponses correctes sur les sujets légers, et ",
        "des résultats inégaux dès que le sujet compte",
        ".",
      ],
      [
        "Le jour où vous la voyez comme ",
        "un système à architecturer",
        ", tout change. Vous ",
        { gras: "construisez le contexte" },
        " dont elle a besoin, une fois, et les bonnes réponses suivent. C'est là que vous devenez ",
        "architecte de votre IA",
        ".",
      ],
      [
        "C'est ce basculement que la journée organise, et c'est ",
        "sa promesse la plus importante",
        ". Tout le reste en découle : le système que vous montez, les techniques que vous emportez, la productivité que vous y gagnez.",
      ],
    ],
    visuels: ["[[PH-04]]"],
  },

  // 6. Comprendre la machine
  comprendreLaMachine: {
    titre: "Vous saurez pourquoi elle dérive, donc quoi corriger.",
    corps: [
      [
        "Un modèle de langage ne comprend pas votre question : il ",
        "complète votre phrase avec la suite la plus probable",
        ". Il ",
        "produit toujours quelque chose",
        ", y compris quand l'information lui manque. Il ",
        "ne garde aucune mémoire",
        " d'une conversation à l'autre. Et il travaille dans ",
        "un espace de lecture limité",
        " qui se sature à mesure que l'échange s'allonge.",
      ],
      "De ces quatre faits découlent tous les comportements que vous connaissez : la conversation qui s'écarte du sujet, la qualité qui décroche au bout d'une heure, la source citée qui n'existe pas.",
      [
        "La matinée commence par là, raconté ",
        { gras: "sans jargon" },
        ", avec ",
        "une conversation dont on affiche tous les appels à l'écran",
        " : quels outils elle utilise, quels fichiers elle lit, quelles tâches elle délègue, dans quel ordre. Vous voyez ce qui se passe pendant qu'elle répond.",
      ],
      [
        "Ce que vous y gagnez est très concret. Devant une réponse ratée, vous saurez ",
        "si le problème vient de votre demande, de votre contexte ou de votre architecture",
        ". Vous cesserez de corriger votre phrase, et vous ",
        { gras: "corrigerez votre système" },
        ".",
      ],
    ],
    visuels: ["[[PH-05]]"],
  },

  // 7. Le Harness
  leHarness: {
    titre: "Le Harness : les trois choses que vous donnez à l'IA.",
    corps: [
      [
        "Elle ne connaît ni vos projets, ni votre organisation, ni votre façon de travailler. À chaque nouvelle conversation, ",
        "vous repartez de zéro",
        " et vous réexpliquez tout.",
      ],
      [
        "Le ",
        { gras: "Harness" },
        " lui donne les trois choses qui lui manquent, et elles vivent toutes ",
        "dans vos fichiers, sur votre ordinateur",
        ". Voici ce que chacune change.",
      ],
    ],
    lesTroisPieces: [
      {
        intitule: "Une mémoire.",
        texte:
          "Qui vous êtes, ce que fait votre organisation, sur quoi porte ce projet, ce qui a été décidé et pourquoi. Écrit une fois, relu par l'IA au début de chaque conversation. Vous ne réexpliquez plus jamais votre contexte.",
      },
      {
        intitule: "Une architecture de suivi.",
        texte:
          "Vos projets rangés dans une structure que l'IA sait parcourir : où en est chaque chantier, ce qui a été fait, ce qui vient ensuite. Vous ouvrez une conversation neuve trois semaines plus tard, vous demandez où on en est, et vous obtenez l'état complet du projet.",
      },
      {
        intitule: "Vos règles de travail.",
        texte:
          "Comment vous voulez qu'on écrive, ce que vos documents doivent respecter, les conventions de votre métier. Écrites une fois, appliquées partout, sans que vous ayez à les redemander.",
      },
    ],
    conclusion: [
      [
        "Ces trois pièces sont des ",
        { gras: "fichiers texte" },
        ", dans vos dossiers. C'est ce qui les rend ",
        "lisibles par vous, exploitables par l'IA, et transposables à n'importe quel outil",
        ".",
      ],
      [
        "Et vous ne les rédigez pas : ",
        "vous les faites écrire",
        ", vous les corrigez, et elles existent ",
        { gras: "en quelques minutes" },
        ".",
      ],
    ],
    visuels: ["[[SVG-01]]", "[[PH-06]]"],
  },

  // 8. La boucle de retroaction
  laBoucleDeRetroaction: {
    titre:
      "La boucle de rétroaction : vous décidez de ce qui sera bon avant que ce soit produit.",
    corps: [
      [
        "Le comportement le plus coûteux de l'IA n'est pas de se tromper. C'est de ",
        "toujours produire quelque chose",
        ", et que ce quelque chose soit ",
        { gras: "plausible" },
        ". Trois paragraphes bien écrits qui ne disent rien passent inaperçus.",
      ],
      [
        "La ",
        { gras: "boucle de rétroaction" },
        " traite cela en quatre temps.",
      ],
    ],
    lesQuatreTemps: [
      {
        intitule: "Les objectifs.",
        texte: "Ce que ce travail doit obtenir, écrit en premier.",
      },
      {
        intitule: "Les critères.",
        texte:
          "Ce qui fera qu'il est bon, écrit avant de produire, en points qui se vérifient un par un.",
      },
      {
        intitule: "La production.",
        texte:
          "L'IA travaille avec les objectifs et les critères sous les yeux.",
      },
      {
        intitule: "L'opposition.",
        texte:
          "Une conversation neuve, qui n'a pas participé à l'écriture, reprend le résultat critère par critère et rend son verdict en citant les passages fautifs.",
      },
    ],
    conclusion: [
      [
        "Ce qui change tient en une phrase : ",
        "le juge n'est plus la conversation qui vient d'écrire le texte",
        ". Votre livrable devient ",
        { gras: "opposable" },
        ", au sens propre du terme, parce qu'il existe un document contre lequel le confronter.",
      ],
      [
        "Cette boucle vaut à ",
        { gras: "trois échelles" },
        " : une tâche, un livrable, et le système lui-même. C'est aussi par elle que ",
        "le Harness se corrige et s'améliore",
        ", ce qui fait de vos critères une pièce de votre mémoire.",
      ],
    ],
    visuels: ["[[SVG-02]]", "[[PH-07]]"],
  },

  // 9. La methodologie
  laMethodologie: {
    titre:
      "Piloter l'IA revient à manager. La méthodologie vous dit quoi faire, et quand.",
    corps: [
      [
        "Ce qu'un bon manager fait avec un assistant, vous allez le faire avec l'IA : définir ce que vous attendez, mémoriser ce qui a été décidé, produire, vérifier, piloter. Un bon manager ",
        "construit en plus le système qui porte ces gestes",
        ", au lieu de les refaire à la main chaque fois.",
      ],
      [
        "Le ",
        { gras: "Harness" },
        " est ce système. La ",
        { gras: "boucle" },
        " est la façon de travailler avec. La ",
        { gras: "méthodologie" },
        " articule les deux et ",
        "dit quand actionner quoi",
        " : quand écrire une règle plutôt que la répéter, quand ouvrir une conversation neuve, quand poser des critères avant de produire, quand faire juger un résultat.",
      ],
      [
        "Je l'ai mise au point ",
        "sur plus de deux ans",
        ", elle ",
        "fait tourner mon activité tous les jours",
        ", et c'est elle que vous verrez tourner pendant la journée. ",
        "Je travaille devant vous sur mes propres projets réels",
        ", pas sur des cas d'école, et ",
        "vous appliquez sur les vôtres au même moment",
        ".",
      ],
    ],
    visuels: ["[[PH-08]]"],
  },

  // 10. Les techniques
  lesTechniques: {
    titre: "Huit gestes que vous saurez refaire lundi matin.",
    introduction: [
      "Une technique détachée d'un objectif s'oublie en une semaine. ",
      "Une technique posée sur un système sert tous les jours.",
      " Voici celles que la journée vous met en main, chacune ",
      { gras: "montrée puis nommée" },
      ".",
    ],
    lesHuitGestes: [
      {
        intitule: "Le fichier porte d'entrée.",
        texte:
          "Le premier fichier que l'IA lit en arrivant dans un projet, celui qui lui dit où elle est et comment on travaille ici.",
      },
      {
        intitule: "La question d'ouverture.",
        texte:
          "Une seule question, posée en début de conversation, qui vous dit immédiatement si votre système tient. Elle change de forme à mesure que le projet a davantage à raconter.",
      },
      {
        intitule: "Une conversation, un sujet.",
        texte:
          "La règle qui protège la qualité : à chaque changement de sujet, une conversation neuve, et le contexte reste net.",
      },
      {
        intitule: "Épurer.",
        texte:
          "Quitter une conversation devenue lourde en emportant son récapitulatif, pour repartir propre sans rien perdre.",
      },
      {
        intitule: "Le handoff.",
        texte:
          "Passer le relais à une conversation neuve avec tout ce qu'il lui faut pour reprendre le travail sans vous.",
      },
      {
        intitule: "La revue indépendante.",
        texte:
          "Faire juger un livrable par une conversation qui ne l'a pas écrit.",
      },
      {
        intitule: "La mémoire dictée.",
        texte:
          "Demander à l'IA ce qu'il faut retenir de ce qui vient de se passer, et le ranger au bon endroit.",
      },
      {
        intitule: "La séparation du fond et de la forme.",
        texte:
          "Écrire le contenu une fois, produire ensuite le document, la présentation ou la page.",
      },
    ],
    conclusion: [
      "Vous repartez avec un document qui les récapitule toutes, avec le vocabulaire et les prompts correspondants.",
      "Un mot sur le Harness lui-même : c'est une approche connue de ceux qui travaillent sérieusement avec l'IA. Celui que je vous montre est le mien, construit et affiné sur plus de deux ans, et c'est celui-là que vous verrez tourner.",
    ],
    visuels: ["[[PH-09]]"],
  },

  // La section « Ce que ca produit » a ete retiree le 2026-08-13, demande de Wahid

  // 11. L'autonomie
  lAutonomie: {
    titre: "Le lundi matin, vous le referez seul.",
    corps: [
      [
        "La promesse de cette journée porte sur ",
        "ce que vous saurez faire",
        ". Vous ",
        "amorcez votre système ici",
        ", avec moi à côté, et vous savez ",
        "le reconstruire seul en rentrant",
        ", sur ce projet ou sur n'importe quel autre.",
      ],
      [
        "C'est pour cela que ",
        { gras: "rien n'est imposé" },
        " pendant la journée. Vous appliquez ce que vous voulez, quand vous le voulez : des temps sont prévus où ",
        "les mains sont libres",
        ", et je circule dans la salle pendant ces temps. ",
        "Regarder et prendre des notes est une manière pleine de suivre la journée.",
        " Ce qui compte est ce que vous saurez faire en sortant.",
      ],
    ],
    ceQueVousGardez: {
      intitule: "Ce que vous gardez au-delà de la journée.",
      texte: [
        "Je travaille devant vous sur ",
        "mes propres projets réels",
        ", et vous appliquez sur ",
        { gras: "les vôtres" },
        " au même moment. Ce que vous montez ce jour-là porte donc sur votre matière, vos dossiers, vos règles : c'est déjà votre système, pas un exercice. Il continue de vous servir la semaine suivante, et vous savez le refaire sur chaque nouveau projet qui arrive.",
      ],
    },
    visuels: ["[[PH-11]]"],
  },

  // 12. Le deroule de la journee
  leDerouleDeLaJournee: {
    titre: "Comment se passe la journée.",
    introduction: [
      [
        "La journée alterne deux régimes. ",
        { gras: "Des temps d'explication" },
        ", pour comprendre comment la machine fonctionne et pourquoi elle se comporte comme elle le fait. ",
        { gras: "Des démonstrations suivies" },
        ", où je fais un geste sur mes propres projets et je l'explique pendant que je le fais.",
      ],
      [
        "Tout ce qui est expliqué ",
        { gras: "sert à agir" },
        " : chaque notion arrive au moment où elle sert, et se retrouve dans ",
        "un geste concret que vous pouvez faire vous-même",
        ".",
      ],
    ],
    lesTroisTemps: [
      {
        intitule: "Matin, premier temps : la machine.",
        texte: [
          "Comment un modèle de langage fonctionne réellement, d'où viennent la dérive, le remplissage et les inventions, et pourquoi ",
          "la réponse tient dans une architecture",
          ". Un socle raconté, qui se termine par ",
          "une conversation entièrement tracée à l'écran",
          ".",
        ],
      },
      {
        intitule: "Matin, second temps : le système s'amorce.",
        texte: [
          "Deux démonstrations suivies, du plus simple au plus riche. Le projet le plus simple du monde, puis ",
          "un dossier en vrac que l'IA comprend, organise et décrit",
          ". Vous appliquez sur votre matière dans la foulée.",
        ],
      },
      {
        intitule: "Après-midi : l'architecture complète, construite de zéro.",
        texte: [
          "Le cœur de la journée. ",
          "Le système entier se construit sous vos yeux",
          ", étape par étape, et ",
          "chacun construit le sien dans le même mouvement",
          ". Puis un temps long où vous consolidez la brique qui vous manque, à votre rythme, avec moi à vos côtés.",
        ],
      },
    ],
    bandeauDeReperes: [
      "9 h 00 à 17 h 00",
      "3 démonstrations suivies",
      "Plus d'un tiers de la journée les mains sur votre clavier",
      "12 à 15 participants",
    ],
    visuels: ["[[PH-12]]"],
  },

  // 13. Pour qui
  pourQui: {
    titre: "Pour l'utilisateur intensif mais artisanal.",
    corps: [
      [
        "Cette journée est pour vous si",
        " vous utilisez ",
        "déjà Claude ou ChatGPT régulièrement",
        ", si vous sentez qu'il y a quelque chose au-delà de la boîte de chat, et si vous voulez appliquer cela à ",
        "vos vrais dossiers professionnels",
        ". Dirigeants, cadres, indépendants, professions de conseil : ",
        "la pratique compte plus que le profil",
        ".",
      ],
      [
        "Elle demande un point d'entrée",
        " : être à l'aise avec votre ordinateur, et ",
        "utiliser l'IA au moins quelques fois par semaine",
        ".",
      ],
      [
        "Elle s'adresse à des professionnels non techniciens.",
        " Aucune ligne de code, à aucun moment.",
      ],
    ],
    visuels: [],
  },

  // 14. Ce qu'il faut preparer
  ceQuIlFautPreparer: {
    titre: "Deux choses à prévoir avant de venir.",
    premierPoint: {
      intitule: "Un abonnement Claude Pro ou Max, actif le jour de la journée.",
      texte: [
        "C'est ",
        { gras: "le seul prérequis" },
        ". Vous travaillez sur votre propre ordinateur avec Claude Cowork, ce qui demande cet abonnement, ",
        "à souscrire avant de venir et à votre charge",
        ".",
      ],
    },
    verification:
      "Vérifiez avant la journée que vous pouvez ouvrir Cowork et y connecter un dossier. Si quelque chose bloque, écrivez-moi et on le règle avant.",
    secondPoint: {
      intitule: "Votre matière de travail, si vous le souhaitez.",
      texte: [
        "La journée reste ",
        "entièrement suivable sans rien apporter",
        ". Elle prend une autre dimension si vous travaillez sur vos propres dossiers : une présentation de vous, une page sur votre activité, un projet en cours, et ",
        "quinze à trente fichiers en désordre",
        " autour de ce projet. Vous recevrez la liste détaillée après votre inscription.",
      ],
    },
    encadre: {
      intitule: "Vos fichiers restent chez vous.",
      texte: [
        "Vous connectez votre dossier vous-même, depuis votre ordinateur, le jour de la formation. ",
        "Rien ne m'est transmis, ni avant, ni pendant.",
        " Vous choisissez la matière avec laquelle vous êtes à l'aise de travailler.",
      ],
    },
    lien: {
      libelle: "écrivez-moi",
      href: "mailto:niya@wahidlahlou.com",
    },
  },

  // 15. Le prix
  lePrix: {
    // LE SIGLE A CHANGE LE 2026-08-19, `contenu-site.md` v17 : le prix affiche est HORS TAXES,
    // decision de Wahid. Le chiffre ne bouge pas, et le site n'ecrit le taux nulle part.
    titre: "4 800 DH HT par personne",
    intituleCompris: "Ce qui est compris",
    compris: [
      "La journée complète, 9 h 00 à 17 h 00",
      "Le déjeuner",
      "Les deux pauses café",
      "Les supports remis sur place",
    ],
    intituleACharge: "Ce qui reste à votre charge",
    aVotreCharge: [
      "L'abonnement Claude Pro ou Max, nécessaire pour travailler pendant la journée",
      "Le transport et l'hébergement",
    ],
    sousLeBloc:
      "Vous venez à plusieurs de la même organisation ? Un tarif de groupe existe, écrivez-moi.",
    mentionPaiement: [
      "Le paiement se fait ",
      "par virement bancaire, avant la journée",
      ". ",
      "Votre virement réserve votre place",
      " : les places se comptent à réception des paiements, et une place payée est une place tenue. Le prix affiché est ",
      { gras: "hors taxes" },
      ".",
    ],
    lien: {
      libelle: "écrivez-moi",
      href: "mailto:niya@wahidlahlou.com",
    },
  },

  // 16. Les dates
  lesDates: {
    titre: "La prochaine session s'ouvre au fil des inscriptions.",
    corps: [
      [
        "Je réunis ",
        "12 à 15 personnes par session",
        ", à Casablanca. ",
        "La date se fixe quand le groupe est constitué",
        " : dès que le nombre de participants confirmés est atteint, je verrouille la date et le lieu, et vous les recevez avec ",
        "un délai confortable pour vous organiser",
        ".",
      ],
      [
        "Concrètement, en trois temps : vous ",
        "demandez votre place maintenant",
        " en indiquant vos disponibilités, je vous écris quand la session se confirme avec sa date, son lieu et les coordonnées bancaires, et ",
        "votre virement réserve votre place",
        ". Les places se comptent à réception des paiements.",
      ],
      "Vous êtes ailleurs qu'à Casablanca ? Indiquez-le dans le formulaire. Les autres villes s'ouvriront en fonction des demandes reçues.",
    ],
    visuels: [],
  },

  // 17. Le formulaire
  leFormulaire: {
    titre: "Je réserve ma place",
    sousTitre: [
      { gras: "Deux minutes" },
      ", et rien à payer maintenant. Je vous écris à la confirmation de la session, et ",
      "votre virement réserve votre place",
      ".",
    ],
    bouton: "Je réserve ma place",
  },

  // 18. FAQ
  faq: {
    questions: [
      {
        question: "Faut-il savoir coder ?",
        reponse: [
          "Aucune ligne de code, à aucun moment.",
          " La journée s'adresse à des professionnels non techniciens.",
        ],
      },
      {
        question:
          "Est-ce que ça marche avec ChatGPT, ou seulement avec Claude ?",
        reponse: [
          "La journée se fait sur ",
          { gras: "Claude Cowork" },
          ", l'outil où cette architecture se construit le plus directement. ",
          "Les principes valent au-delà",
          " : la mémoire écrite, l'architecture de projet et la boucle de rétroaction ",
          "se transposent sur d'autres outils",
          ", et la journée dit lesquels et comment.",
        ],
      },
      {
        question: "Où vont mes données ?",
        reponse: [
          "Voir l'encadré de la section « Deux choses à prévoir avant de venir » : ",
          "vos fichiers restent sur votre ordinateur",
          ", et vous choisissez la matière avec laquelle vous êtes à l'aise de travailler.",
        ],
      },
      {
        question:
          "Je viens sans matière préparée, est-ce que je suis pénalisé ?",
        reponse: [
          "Non. La journée est ",
          "entièrement suivable en regardant et en prenant des notes",
          ", et vous appliquerez chez vous. La matière préparée ajoute de l'effet, ",
          "elle ne conditionne rien",
          ".",
        ],
      },
      {
        question:
          "Et si je n'ai pas le temps de tout faire pendant la journée ?",
        reponse: [
          "C'est prévu, et c'est même ",
          "le cas le plus fréquent",
          ". La promesse porte sur ",
          "ce que vous saurez faire seul en rentrant",
          ", pas sur ce que vous aurez terminé sur place.",
        ],
      },
      {
        question: "Comment se passe le paiement ?",
        reponse:
          // LA DEUXIEME PHRASE SUIT L'ETAPE 4 DU PARCOURS D'INSCRIPTION, recopiee mot pour mot
          // depuis `contenu-site.md` v16 § 18. Elle a change deux fois : le 2026-08-17, une phrase
          // qui annoncait la facture proforma et des coordonnees bancaires affichees, devenues
          // fausses le meme jour, question 023 ; le 2026-08-19, l'annonce d'un appel telephonique,
          // Wahid ayant decide que les informations de paiement partent par email.
          [
            "Par virement bancaire, avant la journée.",
            " Je vous envoie les coordonnées bancaires et le montant à virer par email. ",
            "C'est votre virement qui réserve votre place",
            " : les places se comptent à réception des paiements, dans l'ordre où ils arrivent. ",
            "Aucun paiement sur place.",
          ],
      },
      {
        question: "Que se passe-t-il si je ne peux plus venir ?",
        reponse: [
          "Vous êtes ",
          "remboursé intégralement jusqu'à 14 jours",
          " avant la date annoncée. Entre 14 et 7 jours avant, vous ",
          { gras: "reportez votre place" },
          " sur une session ultérieure. Et à tout moment, vous pouvez ",
          "transmettre votre place à un collègue",
          ".",
        ],
      },
      {
        question: "Y aura-t-il une attestation ?",
        reponse: [
          "Oui. Vous recevez une ",
          "attestation de participation nominative",
          " après la journée.",
        ],
      },
    ],
  },

  // 19. Les temoignages
  lesTemoignages: {
    titre: "Ils ont travaillé avec cette méthode.",
    citations: [
      {
        texte:
          "« Enfin, j'ai l'impression de contrôler véritablement ce que je fais. »",
        auteur: "Hazim Sebbata",
      },
      {
        texte:
          "« Démarche pédagogique très bien pensée. Une vraie formation, pas seulement un vernis. »",
        auteur: "Aymene Mseffer",
      },
      {
        texte:
          "« Une pédagogie claire et structurée, et ce rare talent de rendre des concepts techniques complexes accessibles et concrets. Aucune de mes questions n'est restée sans réponse. Je suis reparti avec des réflexes et des bonnes pratiques que j'applique dès maintenant dans mon travail. »",
        auteur: "Moukaddim Radouane",
      },
      {
        texte:
          "« Superbe formation. On gagne en autonomie, et surtout en harness de ce qu'on produit. Ce n'est pas du jetable, c'est de l'évolutif. »",
        auteur: "Ibtissam Lotfi",
      },
      {
        texte:
          "« Cette formation a vraiment changé ma vision des choses, et elle m'a donné le courage de découvrir mon potentiel. »",
        auteur: "Marouane Lamrabt",
      },
      {
        texte:
          "« Formation très utile pour gagner du temps et mettre en pratique tout ce qui te passe par la tête. »",
        auteur: "Nezha Rahmani",
      },
      {
        texte:
          "« Formation à la fois théorique et très pratique. Demain est certainement un nouveau départ pour mes projets. »",
        auteur: "Soumia El Ibrahimi",
      },
      {
        texte:
          "« Merci infiniment pour ta générosité et ton professionnalisme. J'en ressors avec plein de connaissances et d'idées pratiques à mettre en place. »",
        auteur: "Aziz Slaoui",
      },
      {
        texte:
          "« Cette formation m'a permis de mieux comprendre un univers qui me semblait jusque-là assez complexe. J'en retiens des bases concrètes, la découverte de nouveaux outils, une meilleure compréhension de la manière dont l'IA peut nous aider, et surtout l'envie d'aller plus loin. Merci à Wahid pour sa patience, sa disponibilité et sa pédagogie. »",
        auteur: "Zineb Zilaf",
      },
      {
        texte:
          "« Excellente formation, qui m'a donné les fondamentaux pour gagner en autonomie avec les outils d'IA. »",
        auteur: "Yassine El Idrissi",
      },
      {
        texte:
          "« Formation très utile, qui m'a permis de découvrir tout un monde d'outils. Wahid est bienveillant, et il nous a fait avancer étape par étape. »",
        auteur: "Zendouzi",
      },
      {
        texte:
          "« Une formation très riche sur un sujet qui était du chinois pour moi. J'en ressors capable de travailler par moi-même. »",
        auteur: "Khairat",
      },
      {
        texte: "« Il faudrait qu'il y ait une suite. »",
        auteur: "Said Allali",
      },
      {
        texte: "« Super formation pour démarrer. »",
        auteur: "Fahim Mohammed",
      },
    ],
  },

  // 20. Dernier appel
  dernierAppel: {
    titre:
      "Découvrez le Harness, et révolutionnez votre manière de travailler.",
    corps: [
      "Une journée, 12 à 15 personnes, à Casablanca. Vous en repartez avec ",
      "un système qui tourne",
      " et ",
      "la capacité de le refaire seul",
      ".",
    ],
    bouton: "Je réserve ma place",
    sousLeBouton:
      "4 800 DH HT par personne, déjeuner et pauses compris. Les dates s'ouvrent au fil des inscriptions.",
  },

  // 21. Pied de page
  piedDePage: {
    ligne: "Niya, par Wahid Lahlou · Casablanca",
    contact: {
      intitule: "Contact",
      email: "niya@wahidlahlou.com",
      telephone: "+212 660 617 677",
    },
    linkedin: {
      libelle: "LinkedIn",
      href: "https://www.linkedin.com/in/wahidlahlou",
    },
    pagesAnnexes: {
      intitule: "Pages annexes",
      mentionsLegales: {
        libelle: "Mentions légales",
        href: "/mentions-legales",
      },
      confidentialite: {
        libelle: "Politique de confidentialité",
        href: "/confidentialite",
      },
    },
  },
};

const libellesInterface: LibellesInterface = {
  titreOnglet: "Votre Harness IA, construit en une journée de formation",
  description:
    "Une journée en présentiel à Casablanca, 12 à 15 personnes, sur Claude Cowork, pour construire le système qui rend l'IA fiable sur vos vrais dossiers.",
  descriptionInscription:
    "Choisissez votre session à Casablanca et laissez vos coordonnées en cinq étapes. Rien à payer sur cette page : c'est votre virement qui réserve votre place.",
  descriptionContact:
    "Une question sur la journée « Construisez votre Harness IA » ? Écrivez-moi, je réponds moi-même. Ce formulaire recueille une question, il n'inscrit à rien.",
  descriptionMentionsLegales:
    "L'éditeur du site niya.wahidlahlou.com, son adresse à Casablanca, son identifiant commun de l'entreprise, son hébergeur et le droit qui s'applique.",
  descriptionConfidentialite:
    "Ce que les deux formulaires collectent, pourquoi, avec qui c'est partagé, combien de temps c'est gardé, et comment demander la suppression de vos données.",
  boutonEnvoiEnCours: "Envoi en cours",
  champObligatoireVide: "Ce champ est nécessaire pour vous écrire.",
  emailMalForme: "Cet email semble incomplet.",
  consentementDecoche: "Cochez cette case pour que je puisse vous écrire.",
  lienRetour: "Retour à la page de la journée",
  lienEvitement: "Aller au contenu",
};

/*
 * Les douze emplacements et les deux schemas.
 *
 * Chaque chaine est recopiee mot pour mot, en ouvrant son document source. Le detail de la source
 * de chaque champ est sur le type `Emplacement` ci-dessus. Plus aucune garde ne compare ces
 * chaines a leur spec : la garde INV-TXT-001 a ete retiree du depot le 2026-08-11, et le controle
 * qui reste est la relecture, consignee a /task-done, puis AC-REC-001 a J-05.
 */
const visuels: Visuels = {
  placeholders: {
    // Section 1, Haut de page.
    PH01: {
      rapport: "16/9",
      titre: "Vidéo du haut de page",
      description: "Vidéo courte en boucle, sans son.",
      alt: "Une conversation fraîche s'ouvre sur un vrai workspace, la question « où en est-on ? » est tapée, et la réponse s'écrit avec l'état du projet, les décisions récentes et la prochaine action. Puis l'artefact Cockpit s'affiche.",
    },
    // Section 4, Preuve sociale.
    // Section 5, Le formateur.
    PH03: {
      rapport: "1/1",
      titre: "Portrait de Wahid",
      description: "Portrait de Wahid.",
      alt: "Portrait de Wahid Lahlou.",
    },
    // Section 6, Le changement de mindset.
    PH04: {
      rapport: "16/9",
      titre: "Avec et sans architecture",
      description: "Deux captures côte à côte.",
      alt: "Deux captures côte à côte, même question posée deux fois : à gauche une conversation nue, à droite la même question posée sur un workspace harnessé.",
    },
    // Section 7, Comprendre la machine.
    PH05: {
      rapport: "16/10",
      titre: "La conversation tracée",
      description: "La conversation tracée à l'écran.",
      alt: "Les appels d'agents et d'outils qui défilent à l'écran, et l'emplacement des instructions signalé vide.",
    },
    // Section 8, Le Harness.
    PH06: {
      rapport: "4/5",
      titre: "La mémoire et sa réponse",
      description: "Le fichier de mémoire, puis la réponse qu'il produit.",
      alt: "Le fichier de mémoire ouvert à l'écran, puis la réponse qu'il produit dans une conversation neuve. Deux captures, l'une sous l'autre.",
    },
    // Section 9, La boucle de retroaction.
    PH07: {
      rapport: "16/10",
      titre: "Le rapport de revue",
      description: "Le rapport de revue à l'écran.",
      alt: "Le rapport de revue à l'écran, critère par critère, avec les écarts pointés et les passages fautifs cités.",
    },
    // Section 10, La methodologie.
    PH08: {
      rapport: "16/10",
      titre: "Un workspace réel",
      description: "Vue d'un workspace réel avec son historique de projets.",
      alt: "Vue d'un workspace réel avec son historique de projets.",
    },
    // Section 11, Les techniques.
    PH09: {
      rapport: "3/2",
      titre: "La fiche des techniques",
      description: "La fiche des techniques photographiée sur une table.",
      alt: "La fiche remise en fin de journée, photographiée sur une table.",
    },
    // Section 12, Ce que ca produit.
    // Section 13, L'autonomie.
    PH11: {
      rapport: "16/9",
      titre: "Formateur et participant",
      description: "La vue formateur et la vue d'un participant côte à côte.",
      alt: "La vue formateur et la vue d'un participant côte à côte, au même moment de la journée.",
    },
    // Section 14, Le deroule de la journee.
    PH12: {
      rapport: "3/2",
      titre: "La salle en travail",
      description: "Photo de salle en îlots pendant un temps de travail.",
      alt: "Photo de salle en îlots pendant un temps de travail.",
    },
  },
  schemas: {
    harness: {
      titreAccessible: "Les trois pièces du Harness, schéma en ligne fine.",
      blocs: [
        { intitule: "Une mémoire", detail: "vos projets, vos décisions" },
        {
          intitule: "Une architecture de suivi",
          detail: "où en est chaque chantier",
        },
        { intitule: "Vos règles de travail", detail: "écrites une fois" },
      ],
      rassemblement: "Harness",
      mention: "Des fichiers texte, dans vos dossiers.",
    },
    boucle: {
      titreAccessible: "La boucle en quatre temps, schéma en ligne fine.",
      etapes: [
        { intitule: "Les objectifs", detail: "ce que ce travail doit obtenir" },
        { intitule: "Les critères", detail: "ce qui fera qu'il est bon" },
        {
          intitule: "Produire",
          detail: "avec les objectifs et les critères sous les yeux",
        },
        {
          intitule: "Opposer",
          detail: "une conversation neuve rend son verdict",
        },
      ],
      mention: "le système se corrige",
    },
  },
};

/**
 * LES 29 CHAINES ECRITES PAR LE DEV. Voir le type `RedactionDev` pour la decision qui les autorise
 * et les trois regles qui les encadrent.
 *
 * POUR LA REVUE DE WAHID : tout ce qui est ci-dessous est a lire. Rien d'autre dans ce fichier n'a
 * ete ecrit par le dev. La page `/systeme` reprend ce meme tableau a l'ecran.
 */
const redactionDev: RedactionDev = {
  navigation: {
    mentionMobile: {
      texte: "Formation Harness IA",
      sert: "ce qui s'affiche a droite de la barre haute sur telephone, ou les ancres et le bouton ne tiennent pas",
      origine:
        "ecrit mot pour mot par Wahid le 2026-08-14 : « vue mobile : ajouter Formation Harness IA a droite »",
    },
    nomProduit: {
      texte: "Niya",
      sert: "le nom affiche a gauche de la barre de navigation, et le retour en haut de page",
      origine:
        "tournure du dev. C'est le nom du produit, celui du depot, du sous-domaine niya.wahidlahlou.com et du dossier contenus/_identite-niya. Aucune spec de contenu ne l'ecrit comme un mot a afficher",
    },
    ancres: [
      {
        texte: "Le Harness",
        cible: "#le-harness",
        sert: "ancre 1 de la barre, vers la section 8",
        origine: "nom que contenu-site.md donne a sa section 8, « Le Harness »",
      },
      {
        texte: "La journée",
        cible: "#le-deroule",
        sert: "ancre 2 de la barre, vers la section 14",
        origine:
          "raccourci du nom que contenu-site.md donne a sa section 14, « Le deroule de la journee ». Le nom complet tient mal dans une barre de 64 pixels",
      },
      {
        texte: "Le prix",
        cible: "#le-prix",
        sert: "ancre 3 de la barre, vers la section 17",
        origine: "nom que contenu-site.md donne a sa section 17, « Le prix »",
      },
      {
        texte: "Questions",
        cible: "#faq",
        sert: "ancre 4 de la barre, vers la section 20",
        origine:
          "tournure du dev. contenu-site.md nomme cette section « FAQ », un sigle que Q-014 a retire de la page en tant que titre visible",
      },
    ],
    contact: {
      texte: "Contact",
      cible: "/contact",
      sert: "l entree de contact de la barre de navigation, ajoutee le 2026-08-16",
      origine:
        "le mot que contenu-site.md section 21, Pied de page, donne deja a son bloc de coordonnees, et que le pied de page affiche depuis le 2026-08-15. Reprendre ce mot plutot qu en choisir un autre evite que la page nomme la meme destination de deux facons differentes. La decision d ajouter cette entree est celle de Wahid du 2026-08-16, question Q-018 ; le choix du mot releve de la delegation du 2026-08-12",
    },
    prixBarreMobile: {
      texte: "4 800 DH HT",
      sert: "le rappel de prix porte par la barre d action collante, sur telephone",
      origine:
        "troncature du titre de la section 15 de contenu-site.md, « 4 800 DH HT par personne ». Les trois mots retires ne changent pas le chiffre. Le sigle est passe de TTC a HT le 2026-08-19, avec le titre qu'elle tronque",
    },
  },

  sommaire: {
    titre: "Le sommaire",
  },

  carrousel: {
    precedent: "Témoignages précédents",
    suivant: "Témoignages suivants",
    vue: "Témoignages, vue",
  },

  illustrations: {
    PH01: {
      sert: "PH-01, la conversation qui s ouvre sur un vrai workspace",
      mots: ["projets", "memoire.md", "suivi.md", "regles.md", "notes.md"],
    },
    PH04: {
      sert: "PH-04, la meme question posee sans puis avec architecture",
      mots: ["Sans contexte", "Avec le Harness", "Ou en est-on ?"],
    },
    PH05: {
      sert: "PH-05, les appels d agents et d outils qui defilent",
      mots: [
        "Instructions",
        "aucune",
        "lecture de fichier",
        "memoire.md",
        "recherche dans le projet",
        "suivi.md",
        "appel d outil",
        "lecture de fichier",
        "regles.md",
        "sous-agent : revue",
        "lecture de fichier",
        "criteres.md",
        "ecriture",
        "brouillon.md",
        "verification",
        "sous-agent : opposition",
        "ecriture",
        "livrable.md",
        "reponse",
      ],
    },
    PH06: {
      sert: "PH-06, le fichier de memoire et la reponse qu il produit",
      mots: ["memoire.md", "Projets en cours", "Decisions", "Conventions"],
    },
    PH07: {
      sert: "PH-07, le rapport de revue critere par critere",
      mots: [
        "Rapport de revue",
        "Objectif tenu",
        "Criteres poses avant",
        "Sources verifiees",
        "Ton et registre",
        "Structure attendue",
        "Longueur demandee",
        "Termes du glossaire",
        "Aucune affirmation non sourcee",
        "Format de sortie",
        "Relecture finale",
      ],
    },
    PH08: {
      sert: "PH-08, un workspace reel avec son historique",
      // SIX NOMS DE PROJET DE PLUS LE 2026-08-15, sous la delegation du 2026-08-13 sur les mots des
      // illustrations. Motif : la colonne de gauche portait dix entrees reparties sur 627 pixels, et
      // le dessin doit montrer un workspace REEL, c'est-a-dire une liste qui deborde de la fenetre.
      mots: [
        "Projets",
        "Historique",
        "Cette semaine",
        "La semaine derniere",
        "Le mois dernier",
        "Refonte du site",
        "Rapport annuel",
        "Offre commerciale",
        "Notes de reunion",
        "Budget 2026",
        "Recrutement",
        "Veille",
        "Archives",
        "Contrats",
        "Partenaires",
        "Appels d offres",
        "Facturation",
        "Fournisseurs",
        "Plan de formation",
        "Support client",
        "Comptes rendus",
      ],
    },
    PH11: {
      sert: "PH-11, la vue formateur et celle d un participant",
      mots: ["Vue formateur", "Vue participant", "Etape en cours"],
    },
  },

  surtitres: {
    hautDePage: {
      texte: "La journée",
      sert: "section 1",
      origine:
        "tournure du dev. contenu-site.md nomme cette section « Haut de page », qui decrit un emplacement de gabarit et non un sujet",
    },
    quEstCeQueLeHarness: {
      texte: "Le Harness",
      sert: "section 2",
      origine:
        "nom que contenu-site.md donne a sa section 8, repris ici comme sujet",
    },
    bandeauDesValeurs: {
      texte: "Ce que vous emportez",
      sert: "section 3",
      origine:
        "repris du titre de la section elle-meme, « Ce que vous emportez de cette journee ». Le nom de section de la spec, « Bandeau des sept valeurs », decrit un gabarit",
    },
    leFormateur: {
      texte: "Le formateur",
      sert: "section 4",
      origine: "nom que contenu-site.md donne a sa section 5",
    },
    leChangementDeMindset: {
      texte: "Le changement de mindset",
      sert: "section 5",
      origine: "nom que contenu-site.md donne a sa section 6",
    },
    comprendreLaMachine: {
      texte: "Comprendre la machine",
      sert: "section 6",
      origine: "nom que contenu-site.md donne a sa section 7",
    },
    leHarness: {
      texte: "Les trois pièces",
      sert: "section 7",
      origine:
        "repris du titre de la section elle-meme, « Le Harness : les trois choses que vous donnez a l IA ». Le nom de section, « Le Harness », sert deja de sur-titre a la section 2",
    },
    laBoucleDeRetroaction: {
      texte: "La boucle de rétroaction",
      sert: "section 8",
      origine: "nom que contenu-site.md donne a sa section 9",
    },
    laMethodologie: {
      texte: "La méthodologie",
      sert: "section 9",
      origine: "nom que contenu-site.md donne a sa section 10",
    },
    lesTechniques: {
      texte: "Les techniques",
      sert: "section 10",
      origine: "nom que contenu-site.md donne a sa section 11",
    },
    lAutonomie: {
      texte: "L'autonomie",
      sert: "section 11",
      origine: "nom que contenu-site.md donne a sa section 13",
    },
    leDerouleDeLaJournee: {
      texte: "Le déroulé de la journée",
      sert: "section 12",
      origine: "nom que contenu-site.md donne a sa section 14",
    },
    pourQui: {
      texte: "Pour qui",
      sert: "section 13",
      origine: "nom que contenu-site.md donne a sa section 15",
    },
    ceQuIlFautPreparer: {
      texte: "Ce qu'il faut préparer",
      sert: "section 14",
      origine: "nom que contenu-site.md donne a sa section 16",
    },
    lePrix: {
      texte: "Le prix",
      sert: "section 15",
      origine: "nom que contenu-site.md donne a sa section 17",
    },
    lesDates: {
      texte: "Les dates",
      sert: "section 16",
      origine: "nom que contenu-site.md donne a sa section 18",
    },
    leFormulaire: {
      texte: "Réserver",
      sert: "section 17",
      origine:
        "tournure du dev. contenu-site.md nomme cette section « Le formulaire », qui decrit un gabarit. Le verbe est celui du libelle de bouton de la spec, « Je reserve ma place »",
    },
    faq: {
      texte: "Questions fréquentes",
      sert: "section 18",
      origine:
        "tournure du dev. contenu-site.md nomme cette section « FAQ », un sigle que Q-014 a retire de la page en tant que titre visible",
    },
    lesTemoignages: {
      texte: "Les participants",
      sert: "section 19",
      origine:
        "chaine deja redigee et deleguee le 2026-08-12 pour la section 4. La fusion des deux sections de temoignages, demandee par Wahid le 2026-08-13, la reporte ici : aucun mot nouveau n'est ecrit",
    },
    dernierAppel: {
      texte: "Pour finir",
      sert: "section 20",
      origine:
        "tournure du dev. contenu-site.md nomme cette section « Dernier appel », qui est un terme de metier et non un mot a montrer au lecteur",
    },
    piedDePage: {
      texte: "Niya",
      sert: "section 21",
      origine:
        "tournure du dev. contenu-site.md nomme cette section « Pied de page », qui decrit un emplacement de gabarit. « Contact » a ete ecarte : la section porte deja ce mot comme intitule de son bloc de coordonnees, et le poser aussi en sur-titre l'aurait affiche deux fois de suite. Le nom du produit ferme la page, comme il l'ouvre dans la barre de navigation",
    },
  },
  session: {
    intitule: {
      texte: "La session qui vous arrange",
      sert: "section 17, le choix de session, quand au moins une est ouverte",
      origine:
        "tournure du dev, deleguee par Wahid le 2026-08-14. Elle reprend le verbe de l'aide des disponibilites de contenu-site.md §17, « cochez ce qui vous arrange », pour que les deux blocs se lisent d'une meme voix. « Choisissez votre date » a ete ecarte : le mot choix suppose une place disponible, et c'est le virement qui la reserve",
    },
    aide: {
      texte:
        "Cochez celle qui vous convient. Votre demande n'est pas une place tenue : je vous écris à la confirmation, et c'est votre virement qui réserve.",
      sert: "section 17, sous l intitule du choix de session",
      origine:
        "tournure du dev, deleguee le 2026-08-14. Elle redit la regle de vocabulaire de INVARIANTS.md §3.2 a l'endroit exact ou le lecteur pourrait croire qu'il reserve en cochant. Les mots « demande », « virement » et « reserve » sont ceux de contenu-site.md §17",
    },
    sansPreference: {
      texte: "Aucune ne me convient, écrivez-moi pour une prochaine",
      sert: "section 17, la derniere case du choix de session",
      origine:
        "tournure du dev, deleguee le 2026-08-14. Sans cette case, une personne qu'aucune date n'arrange n'a pas de facon d'envoyer sa demande, et la page perdrait une demande plutot que de la recueillir",
    },
    gabaritHoraire: {
      texte: "de {debut} à {fin}",
      sert: "parcours d inscription, etape 1 et bloc de session du recapitulatif",
      origine:
        "gabarit du dev, sur la delegation de Wahid du 2026-08-15. contenu-site.md v4 § Etape 1 ecrit « l horaire de 9 h 00 a 17 h 00 » dans la liste de ce qu une session affiche : les mots « de » et « a » sont les siens, et les deux heures viennent des colonnes heure_debut et heure_fin, que Wahid edite dans l administration. Les figer a 9 h 00 et 17 h 00 afficherait 9 h 00 sur une session ouverte a 10 h 00",
    },
    gabarit: {
      texte: "{jour} à {lieu}, de {debut} à {fin}",
      sert: "section 17, la ligne de chaque session proposee",
      origine:
        "gabarit du dev, delegue le 2026-08-14. Il porte les quatre valeurs que Wahid saisit dans l'administration, et aucun mot de plus : la date, le lieu et les deux horaires. L'horaire de 9 h a 17 h vient de pilotage-inscriptions.md § 3",
    },
  },
};

/**
 * Le texte des cinq etapes du parcours, recopie de `contenu-site.md` § Le parcours d'inscription.
 *
 * CHAQUE CHAINE SE RETROUVE TELLE QUELLE DANS LE DOCUMENT, et c'est ce qui rend la relecture de
 * Wahid possible : la porte de relecture du sprint 07 est bloquante, et elle se joue sur le
 * document, pas sur ce fichier. Une reformulation ici rendrait les deux impossibles a comparer.
 *
 * LE GRAS DE LA SOURCE N'EST PAS DANS LES CHAINES, comme partout ailleurs dans ce fichier : il
 * afficherait ses asterisques en clair. Le sous-titre de la page porte deux passages en gras dans la
 * spec, et ils se rendent par la structure.
 */
const inscription: ParcoursDInscription = {
  titre: "Je réserve ma place",
  // LE MEMBRE DE PHRASE CENTRAL A CHANGE DEUX FOIS. Le 2026-08-17, `contenu-site.md` v10 : il
  // annoncait « vous y trouverez les coordonnees bancaires », que la page ne porte plus depuis la v7.
  // Le 2026-08-19, `contenu-site.md` v16 : il annoncait un appel telephonique, et Wahid a decide que
  // les informations de paiement partent par email. Les deux fois, le sous-titre promettait, des la
  // premiere ligne, quelque chose que la suite de la page ne fait pas.
  // « Cinq étapes » A ETE RETIRE LE 2026-08-20, `contenu-site.md` v20. Le parcours en compte QUATRE
  // quand aucune date n'est retenue, l'etape du paiement etant sautee, et ce sous-titre s'affiche
  // avant que la personne ait choisi : il ne peut annoncer ni quatre ni cinq. Rien n'a ete ecrit a
  // la place, la phrase perd deux mots.
  sousTitre:
    "Deux minutes. Rien à payer sur cette page : je vous envoie les coordonnées bancaires par email, et votre virement réserve votre place.",
  // LE QUATRIEME REPERE EST « Paiement » DEPUIS LE 2026-08-19, `contenu-site.md` v16, demande de
  // Wahid. Il disait « Le virement », qui nomme le moyen la ou les trois autres nomment une etape.
  fil: [
    "La session",
    "Vos coordonnées",
    "Votre relecture",
    "Paiement",
    "C'est enregistré",
  ],
  etapes: {
    laSession: {
      titre: "Choisissez votre session",
      aide: "Chaque session réunit 12 à 15 personnes, de 9 h 00 à 17 h 00. Choisissez celle qui vous arrange, ou dites-moi que vous attendez une autre date.",
      filtre: {
        intitule: "Filtrer par ville",
        toutesLesVilles: "Toutes les villes",
      },
      places: {
        plusieurs: "Il reste {N} places",
        une: "Il reste 1 place",
        complet: "Complet",
      },
      sansDate: {
        libelle: "Aucune de ces dates ne me convient",
        aide: "Indiquez vos disponibilités à l'étape suivante. Je vous écris dès qu'une session s'ouvre à une date qui vous va.",
      },
      aucuneOuverte:
        "Aucune date n'est ouverte pour le moment. Laissez-moi vos coordonnées et vos disponibilités : je vous écris en premier dès qu'une session s'ouvre.",
      bouton: "Continuer",
      // LES QUATRE INTITULES DE `contenu-site.md` v16 § Etape 1, tableau « Ce qu'une session
      // affiche ». Ils servent aussi au bloc de session du recapitulatif, § Etape 3.
      intitules: {
        date: "Date",
        ville: "Ville",
        horaire: "Horaire",
        lieu: "Lieu",
      },
    },
    vosCoordonnees: {
      titre: "Vos coordonnées",
      // LES SEPT LIBELLES, DANS L'ORDRE DU TABLEAU DE LA SPEC. Les six premiers sont ceux de la
      // section 17, mot pour mot ; la remarque est le seul champ nouveau.
      champs: [
        { libelle: "Prénom et nom", type: "texte", obligatoire: true },
        { libelle: "Email", type: "email", obligatoire: true },
        {
          libelle: "Téléphone (WhatsApp de préférence)",
          type: "téléphone",
          obligatoire: true,
        },
        // LE QUATRIEME CHAMP A CHANGE DE NOM ET DE PORTEE LE 2026-08-20, demande de Wahid : « retirer
        // le champ ville quand le choix de la session est fait et le remplacer par "Ville de
        // preference" quand pas de session disponible ». Il ne s'affiche donc que dans ce cas, et
        // c'est ce qui le rend obligatoire seulement la : voir `schemaDeLaVille` dans
        // `content/schemas.ts`.
        { libelle: "Ville de préférence", type: "liste", obligatoire: true },
        {
          libelle: "Vos disponibilités",
          type: "cases à cocher",
          obligatoire: false,
        },
        { libelle: "Votre remarque", type: "texte long", obligatoire: false },
      ],
      disponibilites: {
        intitule: "Vos disponibilités",
        aide: "Cochez ce qui vous arrange. Cela m'aide à fixer la date.",
        // LES QUATRE OPTIONS DE « QUAND » SONT CELLES DE WAHID, LE 2026-08-20, mot pour mot. Elles
        // etaient trois, « Dans les quatre a six semaines qui viennent », « Dans les deux a trois
        // mois » et « Plus tard ». `contenu-site.md` v19.
        quand: {
          intitule: "Quand",
          options: [
            "Dès que possible",
            "Dans un mois",
            "Dans 2 à 3 mois",
            "Plus tard",
          ],
        },
        // « Plutôt le dimanche » EST NEE LE 2026-08-20, demande de Wahid : « ajouter plutôt le
        // dimanche ». Les deux autres ne bougent pas.
        quelJour: {
          intitule: "Quel jour",
          options: [
            "Plutôt en semaine",
            "Plutôt le samedi",
            "Plutôt le dimanche",
          ],
        },
      },
      remarque: {
        intitule: "Votre remarque",
        aide: "Une contrainte, une question, un besoin particulier pour la journée. Facultatif.",
      },
      villeEntreeVide: "Choisissez une ville",
      // LA MENTION A REMPLACE LA CASE A COCHER LE 2026-08-19, `contenu-site.md` v16 § Etape 2.
      // Demande de Wahid, mot pour mot : « enlever l'option et mettre a la place un message qui dit
      // qu'en continuant, vous acceptez de recevoir les informations de la session par email, par
      // whatsapp ou par telephone ». La tournure finale est ecrite par le dev, sur cette demande, et
      // elle est dans le document : ce fichier la recopie, il ne l'invente pas.
      mentionDeConsentement:
        "En continuant, vous acceptez de recevoir les informations de la session par email, par WhatsApp ou par téléphone.",
      bouton: "Continuer",
      lienRetour: "Revenir au choix de la session",
    },
    votreRelecture: {
      titre: "Relisez avant d'envoyer",
      aide: "Vérifiez chaque bloc. Vous pouvez corriger sans perdre le reste.",
      blocs: [
        { intitule: "La session", lien: "Changer la session" },
        { intitule: "Vos coordonnées", lien: "Changer mes coordonnées" },
      ],
      mentionSansDate: "Vous attendez une prochaine date.",
      bouton: "Envoyer ma demande de place",
      lienRetour: "Revenir à mes coordonnées",
    },
    leVirement: {
      titre: "Votre virement réserve votre place",
      // LES DEUX DERNIERS PARAGRAPHES SONT CEUX DE `contenu-site.md` v16 § Etape 4, ecrits le
      // 2026-08-19. Ils remplacent la consigne de reference du virement et l'annonce de l'appel
      // telephonique : Wahid a decide que les informations de paiement partent par email, et que
      // l'ecran dit ce qui se passe quand une session n'a pas assez d'inscriptions.
      corps: [
        "Votre demande est enregistrée. Elle devient une place tenue à réception de votre virement, et les places se comptent dans l'ordre où les paiements arrivent.",
        "Je vous envoie les coordonnées bancaires et le montant à virer par email, avec la référence à indiquer : votre prénom et votre nom.",
        "Si la session ne réunit pas assez d'inscriptions, elle est annulée et votre virement vous est remboursé.",
      ],
      ceQuiSuit:
        "Vous recevez un email de confirmation dans les minutes qui viennent.",
      bouton: "Terminer",
    },
    cEstEnregistre: {
      titre: "C'est enregistré",
      corpsAvecSession: [
        "Votre demande de place pour la session du {date} à {ville} est enregistrée. Vous recevez un email de confirmation dans les minutes qui viennent.",
        "Votre virement réserve votre place. Dès qu'il arrive, je vous le confirme et je vous envoie le livret de bienvenue.",
      ],
      corpsSansSession: [
        "Votre demande de place est enregistrée, et vos disponibilités avec. Vous recevez un email de confirmation dans les minutes qui viennent.",
        "Je vous écris dès qu'une session s'ouvre à une date qui vous va, avec le lieu. C'est votre virement qui réserve votre place.",
      ],
      sousLeCorps:
        "Une question d'ici là ? Écrivez-moi à niya@wahidlahlou.com, ou sur WhatsApp au +212 660 617 677.",
      lien: "Retour à la page de la journée",
    },
  },
  erreurs: {
    champObligatoireVide: "Ce champ est nécessaire pour vous écrire.",
    emailMalForme: "Cet email semble incomplet.",
    boutonEnvoiEnCours: "Envoi en cours",
    aucuneSessionChoisie:
      "Choisissez une session, ou indiquez qu'aucune date ne vous convient.",
    sessionComplete:
      "Cette session est complète. Choisissez une autre date, ou indiquez qu'aucune ne vous convient.",
    sessionFermee:
      "Cette session n'est plus ouverte aux inscriptions. Choisissez une autre date.",
    dejaInscrit:
      "Vous avez déjà demandé une place pour cette journée. Si ce n'est pas ce que vous pensiez, " +
      "écrivez-moi à niya@wahidlahlou.com, ou sur WhatsApp au +212 660 617 677.",
  },
  echecTechnique:
    "L'envoi n'a pas abouti. Réessayez dans un instant, ou écrivez-moi à niya@wahidlahlou.com et je vous inscris à la main.",
};

/**
 * Le formulaire de contact, recopie du document, ligne par ligne.
 *
 * LES TROIS PREMIERS LIBELLES SONT CEUX DE LA SECTION 17 DE LA v3, MOT POUR MOT, et le document le
 * dit lui-meme. Le quatrieme, « Votre message », est le seul champ que ce formulaire ajoute.
 *
 * LE TELEPHONE EST LE SEUL CHAMP FACULTATIF : c'est la colonne « Obligatoire » du tableau, et
 * `spec-technique.md` v6 § contacts l'ecrit une seconde fois, « facultatif. Une question se repond
 * par email ».
 */
const contact: FormulaireDeContact = {
  titre: "Une question ?",
  sousTitre:
    "Écrivez-moi. Je réponds moi-même, et ce formulaire ne vous inscrit à rien.",
  champs: [
    { libelle: "Prénom et nom", type: "texte", obligatoire: true },
    { libelle: "Email", type: "email", obligatoire: true },
    {
      libelle: "Téléphone (WhatsApp de préférence)",
      type: "téléphone",
      obligatoire: false,
    },
    { libelle: "Votre message", type: "texte long", obligatoire: true },
  ],
  aideDuMessage: "Dites-moi ce que vous cherchez à savoir. Une ligne suffit.",
  bouton: "Envoyer ma question",
  messages: {
    confirmation:
      "C'est envoyé. Je vous réponds par email. Vous n'êtes inscrit à rien : si vous voulez une place, passez par la page d'inscription.",
    echec:
      "L'envoi n'a pas abouti. Réessayez dans un instant, ou écrivez-moi directement à niya@wahidlahlou.com.",
  },
  lien: "Retour à la page de la journée",
};

/*
 * LES DEUX PAGES ANNEXES, recopiees de `contenu-site.md` v11.
 *
 * LES INTITULES DE RUBRIQUE S'AFFICHENT, ce que le type `RubriqueAnnexe` explique. Les deux
 * intitules `###` du document qui NE s'affichent PAS sont « Le titre de la page » et « Lien » : le
 * premier nomme le titre, le second nomme le lien de retour, et ils vivent tous deux sur leur propre
 * champ ici plutot que dans la liste des rubriques.
 *
 * LE TELEPHONE S'ECRIT AVEC DES ESPACES, `+212 660 617 677`, et non avec les points de la forme sous
 * laquelle Wahid l'a transmis. Le pied de page et le parcours d'inscription l'ecrivent ainsi depuis
 * le 2026-08-11 : le site ne montre pas le meme numero sous deux formes.
 */
const pagesAnnexes: PagesAnnexes = {
  mentionsLegales: {
    titre: "Mentions légales",
    rubriques: [
      {
        intitule: "L'éditeur du site",
        paragraphes: [
          "Le site https://niya.wahidlahlou.com est édité par W20 SARL AU, société à responsabilité limitée à associé unique de droit marocain.",
          "37, rue Mohamed Smiha, étage 10, n° 57\n20100 Casablanca, Maroc",
          "ICE : 00352908000047",
          "Email : niya@wahidlahlou.com\nTéléphone : +212 660 617 677",
        ],
      },
      {
        intitule: "Le directeur de la publication",
        paragraphes: ["Wahid Lahlou."],
      },
      {
        intitule: "L'hébergement",
        paragraphes: [
          "Le site est hébergé par Vercel Inc., 440 N Barranca Ave #4133, Covina, CA 91723, États-Unis.",
          "Les données recueillies par les formulaires sont stockées par Neon Inc., aux États-Unis. Les emails partent par Brevo, Sendinblue SAS, 7 rue de Madrid, 75008 Paris, France. La mesure d'audience est faite par PostHog, sur ses serveurs européens, à Francfort.",
        ],
      },
      {
        intitule: "La propriété intellectuelle",
        paragraphes: [
          "Les textes, les illustrations et la mise en page de ce site appartiennent à W20 SARL AU. Leur reproduction, même partielle, demande son accord écrit.",
        ],
      },
      {
        intitule: "Les données personnelles",
        paragraphes: [
          "Ce que les formulaires collectent, pourquoi, où les données vivent et comment en demander la suppression sont écrits sur la page « Politique de confidentialité ».",
        ],
      },
      {
        intitule: "Le droit applicable",
        paragraphes: ["Ce site est soumis au droit marocain."],
      },
    ],
    lien: "Retour à la page de la journée",
  },
  confidentialite: {
    titre: "Politique de confidentialité",
    rubriques: [
      {
        intitule: "En deux phrases",
        paragraphes: [
          "Vos données me servent à organiser la journée et à vous écrire. Elles ne sont partagées avec personne, et elles ne sont vendues à personne.",
        ],
      },
      {
        intitule: "Ce que je collecte, et quand",
        paragraphes: [
          "Deux formulaires recueillent des données, et eux seuls.",
          "Quand vous demandez une place, sur la page d'inscription : votre prénom et votre nom, votre email, votre téléphone, votre ville. Vos disponibilités et votre remarque sont facultatives, et la case de consentement est obligatoire.",
          "Quand vous posez une question, sur la page de contact : votre prénom et votre nom, votre email, votre message. Votre téléphone est facultatif.",
          "Aucun autre champ n'est collecté. Votre navigation est comptée, sans être rattachée à vous : la rubrique « Les cookies et la mesure d'audience » dit exactement ce qui l'est.",
        ],
      },
      {
        intitule: "Pourquoi je les collecte",
        paragraphes: [
          "Pour la logistique de la journée, et pour rien d'autre : vous répondre, vous écrire quand une session s'ouvre ou se confirme, tenir la liste des personnes inscrites, et vous appeler pour vous donner les coordonnées du virement.",
        ],
      },
      {
        intitule: "Avec qui elles sont partagées",
        paragraphes: [
          "Avec personne. Je ne les vends pas, je ne les échange pas, et je ne m'en sers pour aucune autre raison.",
          "Trois prestataires les hébergent pour moi, et eux seuls : l'hébergeur du site, l'hébergeur de la base de données, et le service qui envoie les emails. Ils sont nommés sur la page « Mentions légales ». Personne d'autre n'y a accès que moi. La mesure d'audience, elle, ne reçoit aucune de ces données.",
        ],
      },
      {
        intitule: "Combien de temps",
        paragraphes: [
          "Sans limite fixée à l'avance. Vos données restent tant que vous ne m'en demandez pas la suppression.",
        ],
      },
      {
        intitule: "Les cookies et la mesure d'audience",
        paragraphes: [
          "Ce site ne dépose aucun cookie sur votre appareil, et il ne vous demande donc aucun consentement.",
          "Une mesure d'audience compte les pages vues et les clics sur le bouton d'inscription. Elle est faite par PostHog, sur ses serveurs européens, et elle est réglée pour ne rien écrire sur votre appareil : ni cookie, ni stockage local. Elle ne permet pas de vous reconnaître d'une visite à l'autre, et elle n'enregistre ni votre nom, ni votre email, ni ce que vous saisissez dans un formulaire.",
        ],
      },
      {
        intitule: "Vos droits",
        paragraphes: [
          "Vous pouvez à tout moment demander à voir les données que je détiens sur vous, les faire corriger, vous opposer à leur utilisation, ou en demander la suppression.",
          "Écrivez-moi à niya@wahidlahlou.com. Je réponds moi-même, et je vous confirme par email ce que j'ai fait.",
        ],
      },
      {
        intitule: "Le cadre légal",
        paragraphes: [
          "Le traitement de ces données est soumis à la loi marocaine 09-08 sur la protection des personnes physiques à l'égard du traitement des données à caractère personnel.",
        ],
      },
    ],
    lien: "Retour à la page de la journée",
  },
  /*
   * LA PAGE 404, RECOPIEE DE `contenu-site.md` v12 § La page 404.
   *
   * LE TITRE ET LE MESSAGE SONT DE WAHID, ET NON DU DEV. Il a demande la page le 2026-08-17 et il en
   * a choisi le texte dans la conversation, parmi trois couples titre et phrase que le dev lui a
   * proposes. C'est ce choix qui a leve le blocage : jusqu'a ce jour, ces deux textes ne figuraient
   * dans aucun document opposable, et la page n'existait pas pour ce seul motif.
   *
   * LE LIBELLE DU LIEN EST CELUI DES DEUX AUTRES PAGES ANNEXES, ecrit au § Les libelles d'interface,
   * ligne « Lien de retour depuis les pages annexes ». Il est recopie et non partage : les trois
   * pages le lisent chacune dans leur propre cle, comme le document les ecrit trois fois.
   */
  introuvable: {
    titre: "Cette page n'existe pas",
    message:
      "Le lien que vous avez suivi est peut-être ancien, ou l'adresse comporte une faute de frappe.",
    lien: "Retour à la page de la journée",
  },
};

/**
 * Le texte de la page d'annulation, recopie de `contenu-site.md` v21 § La page d'annulation.
 *
 * ⚠️ AUCUNE DE CES CHAINES N'EST INVENTEE ICI. Elles sont ecrites dans le document par le dev, sur la
 * delegation que Wahid a donnee le 2026-08-20, « je redige, tu valides apres », et elles y attendent
 * sa relecture. Les modifier ici sans modifier le document ferait diverger les deux.
 */
const annulation: PageDAnnulation = {
  titre: "Annuler votre inscription",
  sousTitre:
    "Vous êtes sur le point d'annuler votre place pour la journée « Construisez votre Harness IA ».",
  rappel: {
    titre: "Votre inscription",
    jour: "Date",
    ville: "Ville",
    lieu: "Lieu",
    sansDate: "Aucune date retenue",
  },
  etats: {
    sansVirement: {
      texte:
        "Vous n'avez encore rien viré. Votre place se libère tout de suite, et il n'y a rien d'autre à faire.",
      bouton: "Annuler mon inscription",
    },
    remboursement: {
      texte:
        "Vous êtes remboursé intégralement. Je fais le virement de retour et je vous écris pour vous le confirmer.",
      bouton: "Annuler et demander le remboursement",
    },
    report: {
      texte:
        "La journée est dans moins de 14 jours : votre place se reporte sur une session ultérieure. Je vous écris dès qu'une date se fixe.",
      bouton: "Reporter ma place",
    },
    tropTard: {
      texte:
        "La journée est dans moins de 7 jours. Écrivez-moi à niya@wahidlahlou.com, ou sur WhatsApp au +212 660 617 677, et nous voyons ensemble.",
    },
  },
  apres: {
    sansVirement:
      "C'est annulé. Votre place est libérée, et votre inscription reste enregistrée : si une prochaine session vous convient, vous êtes parmi les premiers prévenus.",
    remboursement:
      "C'est annulé. Je fais le virement de retour et je vous écris pour vous le confirmer.",
    report:
      "C'est noté. Votre place est reportée, et je vous écris dès qu'une prochaine session s'ouvre.",
    dejaAnnulee:
      "Cette inscription est déjà annulée. Si vous voulez revenir dessus, écrivez-moi à niya@wahidlahlou.com, ou sur WhatsApp au +212 660 617 677.",
    lienInconnu:
      "Ce lien ne correspond à aucune inscription. Vérifiez que vous l'avez recopié en entier, ou écrivez-moi à niya@wahidlahlou.com.",
    echec:
      "L'annulation n'a pas abouti. Réessayez dans un instant, ou écrivez-moi à niya@wahidlahlou.com et je l'annule à la main.",
  },
};

export const contenu: Contenu = {
  sections,
  inscription,
  contact,
  pagesAnnexes,
  interface: libellesInterface,
  visuels,
  redactionDev,
  annulation,
  emails,
};

export default contenu;
